import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  useMediaQuery,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Divider,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Snackbar,
  Alert,
  Autocomplete,
} from "@mui/material";
import {
  LocationOn,
  DeviceHub,
  Receipt,
  WaterDrop,
  ArrowBack as ArrowBackIcon,
  CalendarToday,
  Search,
  Clear,
  FileDownload,
  Edit,
  Delete,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { Bolt } from '@mui/icons-material';
import dashboardService from "../services/dashboardService";
import siteService from "../services/siteService";
import deviceService from "../services/deviceService";
import { useAuth } from "../context/AuthContext";
import { format, addDays, subDays } from 'date-fns';
import { downloadSiteDevicesCSV } from '../utils/downloadHelper';
import { estimateLeakLitresPerHour } from '../utils/leakEstimate';
import DeviceFlowChart from "../components/DeviceFlowChart";
import StructuredLocationFields from "../components/StructuredLocationFields";
import NotificationTable from '../components/notifications/NotificationTable';
import "../styles/NetworkGraph.css";

import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineController,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Filler,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

const barValueLabelsPlugin = {
  id: 'barValueLabelsV4',
  beforeDraw: (chart) => {
    const pluginOptions = chart?.config?.options?.plugins?.[barValueLabelsPlugin.id] ?? chart?.options?.plugins?.[barValueLabelsPlugin.id];
    if (!pluginOptions || pluginOptions.display === false) return;

    const stateBag = chart.$_barValueLabelsState || (chart.$_barValueLabelsState = {});
    stateBag[barValueLabelsPlugin.id] = {
      seenKeys: new Set()
    };
  },
  afterDraw: (chart) => {
    // no-op
  },
  afterDatasetsDraw: (chart) => {
    const pluginOptions = chart?.config?.options?.plugins?.[barValueLabelsPlugin.id] ?? chart?.options?.plugins?.[barValueLabelsPlugin.id];
    if (!pluginOptions || pluginOptions.display === false) return;

    const stateBag = chart.$_barValueLabelsState || (chart.$_barValueLabelsState = {});
    const state = stateBag[barValueLabelsPlugin.id] || (stateBag[barValueLabelsPlugin.id] = { seenKeys: new Set() });

    const ctx = chart.ctx;
    const color = pluginOptions.color || '#333';
    const insideColor = pluginOptions.insideColor;
    const lineColor = pluginOptions.lineColor;
    const fontSize = pluginOptions.fontSize || 10;
    const fontFamily = pluginOptions.fontFamily;
    const offset = Number.isFinite(pluginOptions.offset) ? pluginOptions.offset : 4;
    const minValue = Number.isFinite(pluginOptions.minValue) ? pluginOptions.minValue : 0;
    const position = pluginOptions.position || 'top';
    const positionByDatasetIndex = pluginOptions.positionByDatasetIndex;
    const colorByDatasetIndex = pluginOptions.colorByDatasetIndex;
    const insideColorByDatasetIndex = pluginOptions.insideColorByDatasetIndex;
    const includeLineDatasets = pluginOptions.includeLineDatasets === true;
    const formatter = typeof pluginOptions.formatter === 'function'
      ? pluginOptions.formatter
      : (v) => {
          const num = Number(v);
          if (!Number.isFinite(num)) return '';
          return Math.round(num).toLocaleString('en-US');
        };

    const drawDatasetLabels = (datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;
      if (meta.type !== 'bar' && meta.type !== 'line') return;
      if (meta.type === 'line' && !includeLineDatasets) return;

      const dataset = chart.data?.datasets?.[datasetIndex];
      if (meta.type === 'line' && dataset?.yAxisID === 'y1') return;

      meta.data.forEach((element, index) => {
        const raw = dataset?.data?.[index];
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        if (value <= minValue) return;

        const pos = element.tooltipPosition ? element.tooltipPosition() : { x: element.x, y: element.y };
        const label = formatter(value, { datasetIndex, index, chart });
        if (!label) return;

        const seenKey = `${datasetIndex}:${index}:${label}:${Math.round(pos.x)}:${Math.round(pos.y)}`;
        if (state.seenKeys?.has(seenKey)) return;
        state.seenKeys?.add(seenKey);

        const baseY = Number.isFinite(element?.base) ? element.base : pos.y;
        const barTop = Math.min(pos.y, baseY);
        const barBottom = Math.max(pos.y, baseY);
        const barHeight = barBottom - barTop;

        const effectivePosition =
          positionByDatasetIndex && Object.prototype.hasOwnProperty.call(positionByDatasetIndex, datasetIndex)
            ? positionByDatasetIndex[datasetIndex]
            : position;
        const effectiveColor =
          colorByDatasetIndex && Object.prototype.hasOwnProperty.call(colorByDatasetIndex, datasetIndex)
            ? colorByDatasetIndex[datasetIndex]
            : color;
        const effectiveInsideColor =
          insideColorByDatasetIndex && Object.prototype.hasOwnProperty.call(insideColorByDatasetIndex, datasetIndex)
            ? insideColorByDatasetIndex[datasetIndex]
            : insideColor;

        let x = pos.x;
        let y = pos.y - offset;
        let fill = effectiveColor;
        let baseline = 'bottom';

        if (meta.type === 'bar') {
          if (effectivePosition === 'middle') {
            if (barHeight < fontSize + 6) {
              y = barBottom + offset;
              baseline = 'top';
            } else {
              y = barTop + (barHeight / 2);
              baseline = 'middle';
              fill = effectiveInsideColor || effectiveColor;
            }
          } else if (effectivePosition === 'insideTop') {
            if (barHeight < fontSize + 6) {
              y = barTop - offset;
              baseline = 'bottom';
            } else {
              y = barTop + 2;
              baseline = 'top';
              fill = effectiveInsideColor || effectiveColor;
            }
          } else if (effectivePosition === 'inside') {
            if (barHeight < fontSize + 6) {
              y = barBottom + offset;
              baseline = 'top';
            } else {
              y = barTop + 2;
              baseline = 'top';
              fill = effectiveInsideColor || effectiveColor;
            }
          } else if (effectivePosition === 'bottom') {
            y = barBottom + offset;
            baseline = 'top';
          } else {
            y = barTop - offset;
            baseline = 'bottom';
          }
        } else {
          fill = lineColor || effectiveColor;
          y = pos.y - offset;
          baseline = 'bottom';
        }

        ctx.save();
        ctx.fillStyle = fill;
        ctx.textAlign = 'center';
        ctx.textBaseline = baseline;
        ctx.font = `${fontSize}px ${fontFamily || 'sans-serif'}`;
        ctx.fillText(label, x, y);
        ctx.restore();
      });
    };

    for (let i = 0; i < (chart.data?.datasets?.length || 0); i++) {
      drawDatasetLabels(i);
    }
  }
};

ChartJS.register(
  LineController,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Filler,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const ensureBarValueLabelsPluginRegistered = () => {
  if (ChartJS.__barValueLabelsV4PluginRegistered) return;
  ChartJS.register(barValueLabelsPlugin);
  ChartJS.__barValueLabelsV4PluginRegistered = true;
};

ensureBarValueLabelsPluginRegistered();

// Utility function for formatting
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const findFirstHierarchyNodeWithChildren = (nodes, predicate = () => true) => {
  const stack = Array.isArray(nodes) ? [...nodes] : [];
  while (stack.length > 0) {
    const node = stack.shift();
    if (!node) continue;
    const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
    if (hasChildren && predicate(node)) return node;
    if (Array.isArray(node?.children) && node.children.length > 0) {
      stack.push(...node.children);
    }
  }
  return null;
};

const BULK_METER_SITE_IDS_METADATA_KEY = 'bulkMeterSiteIds';

const normalizeStringArray = (value) => {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.map((v) => String(v)).filter(Boolean)));
};

const getBulkMeterSiteIdsFromDevice = (device) => {
  return normalizeStringArray(device?.metadata?.[BULK_METER_SITE_IDS_METADATA_KEY]);
};

const isDeviceBulkMeterForSite = (device, siteId) => {
  if (!siteId) return false;
  const siteIds = getBulkMeterSiteIdsFromDevice(device);
  return siteIds.includes(String(siteId));
};

const SiteDetails = () => {
  const monitorLastDayEnabled = false;

  // Basic hooks
  const { id: siteId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: authUser } = useAuth();

  const chartTextColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.92)'
    : theme.palette.text.primary;
  const chartGridColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.16)'
    : theme.palette.divider;
  const chartTooltipBgColor = theme.palette.mode === 'dark'
    ? 'rgba(33, 33, 33, 0.92)'
    : 'rgba(255, 255, 255, 0.98)';
  const chartTooltipTextColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.92)'
    : 'rgba(0, 0, 0, 0.87)';
  const chartTooltipBorderColor = theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.18)'
    : 'rgba(0, 0, 0, 0.12)';
  
  // Media queries
  const isMobile = useMediaQuery('(max-width:600px)');
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const iconSize = isSmallScreen ? '1.25rem' : '1.5rem';
  
  // State declarations
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState(null);
  const [devices, setDevices] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0: Monitor, 1: Notifications, 2: Network
  const [usageSubTab, setUsageSubTab] = useState('water'); // 'water' | 'electrical'
  const [dashboardData, setDashboardData] = useState({ todayDate: "" });
  const [deviceHierarchy, setDeviceHierarchy] = useState([]);

  const [siteCsvExporting, setSiteCsvExporting] = useState(false);

  const [monitorLoading, setMonitorLoading] = useState(false);
  const [monitorError, setMonitorError] = useState(null);
  const [monitorDates, setMonitorDates] = useState([]);
  const [monitorTotalSeries, setMonitorTotalSeries] = useState([]);
  const [monitorBulkSeries, setMonitorBulkSeries] = useState(null);
  const [monitorBulkBreakdown, setMonitorBulkBreakdown] = useState([]);
  const [monitorTopDeviceLabel, setMonitorTopDeviceLabel] = useState('');
  const [monitorTopDeviceTotal, setMonitorTopDeviceTotal] = useState(null);
  const [monitorLastDayTopDeviceLabel, setMonitorLastDayTopDeviceLabel] = useState('');
  const [monitorLastDayTopDeviceTotal, setMonitorLastDayTopDeviceTotal] = useState(null);
  const [monitorLastDayTopConsumers, setMonitorLastDayTopConsumers] = useState([]);
  const [monitorLastDayUsageByDeviceId, setMonitorLastDayUsageByDeviceId] = useState({});
  const [monitorTopConsumers, setMonitorTopConsumers] = useState([]);
  const [monitorLastActiveDateKey, setMonitorLastActiveDateKey] = useState(null);
  const [monitorShowLastDay, setMonitorShowLastDay] = useState(false);
  const [monitorShowLast14Days, setMonitorShowLast14Days] = useState(false);
  const [monitorNotificationCountsByDate, setMonitorNotificationCountsByDate] = useState([]);
  const [monitorDataUpdatedAt, setMonitorDataUpdatedAt] = useState(null);

  const [connectedDeviceSearch, setConnectedDeviceSearch] = useState('');
  const [connectedDeviceSortByUsage, setConnectedDeviceSortByUsage] = useState(false);
  const [pendingNotificationCountByDeviceId, setPendingNotificationCountByDeviceId] = useState({});
  const [pendingNotificationTypeCountByDeviceId, setPendingNotificationTypeCountByDeviceId] = useState({});

  const [monitorNotifSummaryLoading, setMonitorNotifSummaryLoading] = useState(false);
  const [monitorActiveLeaks, setMonitorActiveLeaks] = useState(0);
  const [monitorActiveLowSignal, setMonitorActiveLowSignal] = useState(0);
  const [monitorActiveThresholds, setMonitorActiveThresholds] = useState(0);
  const [monitorLastDayLeaks, setMonitorLastDayLeaks] = useState(0);
  const [monitorLastDayLowSignal, setMonitorLastDayLowSignal] = useState(0);
  const [monitorLastDayThresholds, setMonitorLastDayThresholds] = useState(0);

  const formatAlertTypeLabel = useCallback((type) => {
    if (type === 'leak_detected' || type === 'high_usage') return 'Leak';
    if (type === 'signal_strength') return 'Signal';
    if (type === 'threshold_exceeded') return 'Threshold';
    return 'Alert';
  }, []);
  const [monitorLast14DayNotifications, setMonitorLast14DayNotifications] = useState([]);
  const [monitorLastDayNotifications, setMonitorLastDayNotifications] = useState([]);

  const monitorSummaryLastLoggedRef = useRef('');
  const monitorLoadLastKeyRef = useRef('');
  const monitorNotifLoadLastKeyRef = useRef('');
  const monitorAutoExpandLast14Ref = useRef('');
  const monitorWindowDays = 7;
  const connectedDevicesRef = useRef(null);

  const [siteNotificationsLoading, setSiteNotificationsLoading] = useState(false);
  const [siteNotificationsError, setSiteNotificationsError] = useState(null);
  const [siteNotifications, setSiteNotifications] = useState([]);
  const [siteNotificationsType, setSiteNotificationsType] = useState('');
  const [siteNotificationsStatus, setSiteNotificationsStatus] = useState('');

  const restorePayload = useMemo(() => location?.state?.restore || null, [location?.state]);
  const restoreScrollY = location?.state?.restoreScrollY;
  const didRestoreRef = useRef(false);

  const [siteEditOpen, setSiteEditOpen] = useState(false);
  const [siteDeleteOpen, setSiteDeleteOpen] = useState(false);
  const [siteEditSaving, setSiteEditSaving] = useState(false);
  const [siteEditForm, setSiteEditForm] = useState({
    description: '',
    location: '',
    latitude: '',
    longitude: '',
    leakNightFlowThresholdLph: '',
    leakNightContinuousHours: '',
    leakNightStartHour: '',
    leakNightEndHour: '',
    signalStrengthThresholdReadings: ''
  });
  const [siteActionSnackbar, setSiteActionSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const isAdmin = authUser?.role === 'admin';
  const isEndUser = authUser?.role === 'end_user';
  const canConfigureBulkMeter = isAdmin || authUser?.role === 'operator';

  const [networkBulkMeterIds, setNetworkBulkMeterIds] = useState([]);
  const [networkBulkMeterSaving, setNetworkBulkMeterSaving] = useState(false);
  const [networkParentAssignments, setNetworkParentAssignments] = useState({});
  const [networkParentSaving, setNetworkParentSaving] = useState(false);

  const monitorLastDayTopLeaks = useMemo(() => {
    const getLeakHours = (n) => Number(n?.data?.continuousNightHours) || 0;
    const getLeakSize = (n) => Number(estimateLeakLitresPerHour(n)) || 0;
    const leaks = (monitorLastDayNotifications || []).filter(
      (n) => String(n?.type || '') === 'leak_detected'
    );
    return [...leaks]
      .sort((a, b) => {
        const sizeDelta = getLeakSize(b) - getLeakSize(a);
        if (sizeDelta !== 0) return sizeDelta;
        return getLeakHours(b) - getLeakHours(a);
      })
      .slice(0, 10)
      .map((n) => ({
        id: n.id,
        deviceId: n?.deviceId ?? n?.device?.id ?? null,
        serialNumber: n?.device?.serialNumber || 'N/A',
        description: n?.device?.description || '-',
        leakHours: getLeakHours(n),
        estLitresPerHour: estimateLeakLitresPerHour(n)
      }));
  }, [monitorLastDayNotifications]);

  const monitor14DayTopLeaks = useMemo(() => {
    const getLeakHours = (n) => Number(n?.data?.continuousNightHours) || 0;
    const getLeakSize = (n) => Number(estimateLeakLitresPerHour(n)) || 0;
    const leaks = (monitorLast14DayNotifications || []).filter(
      (n) => String(n?.type || '') === 'leak_detected'
    );
    return [...leaks]
      .sort((a, b) => {
        const sizeDelta = getLeakSize(b) - getLeakSize(a);
        if (sizeDelta !== 0) return sizeDelta;
        return getLeakHours(b) - getLeakHours(a);
      })
      .slice(0, 10)
      .map((n) => ({
        id: n.id,
        deviceId: n?.deviceId ?? n?.device?.id ?? null,
        serialNumber: n?.device?.serialNumber || 'N/A',
        description: n?.device?.description || '-',
        leakHours: getLeakHours(n),
        estLitresPerHour: estimateLeakLitresPerHour(n)
      }));
  }, [monitorLast14DayNotifications]);

  const openEditSiteDialog = useCallback(() => {
    if (isEndUser) return;
    setSiteEditForm({
      description: site?.description || '',
      location: site?.location || '',
      latitude: site?.latitude !== undefined && site?.latitude !== null ? String(site.latitude) : '',
      longitude: site?.longitude !== undefined && site?.longitude !== null ? String(site.longitude) : '',
      leakNightFlowThresholdLph: site?.leakNightFlowThresholdLph !== undefined && site?.leakNightFlowThresholdLph !== null ? String(site.leakNightFlowThresholdLph) : '0.5',
      leakNightContinuousHours: site?.leakNightContinuousHours !== undefined && site?.leakNightContinuousHours !== null ? String(site.leakNightContinuousHours) : '4',
      leakNightStartHour: site?.leakNightStartHour !== undefined && site?.leakNightStartHour !== null ? String(site.leakNightStartHour) : '0',
      leakNightEndHour: site?.leakNightEndHour !== undefined && site?.leakNightEndHour !== null ? String(site.leakNightEndHour) : '24',
      signalStrengthThresholdReadings: site?.signalStrengthThresholdReadings !== undefined && site?.signalStrengthThresholdReadings !== null ? String(site.signalStrengthThresholdReadings) : '16'
    });
    setSiteEditOpen(true);
  }, [isEndUser, site]);

  const openDeleteSiteDialog = useCallback(() => {
    if (!isAdmin) return;
    setSiteDeleteOpen(true);
  }, [isAdmin]);

  const handleSiteEditSave = useCallback(async () => {
    if (!siteId) return;
    try {
      setSiteEditSaving(true);
      const payload = {
        description: siteEditForm.description,
        location: siteEditForm.location,
        latitude: siteEditForm.latitude === '' ? null : Number(siteEditForm.latitude),
        longitude: siteEditForm.longitude === '' ? null : Number(siteEditForm.longitude),
        leakNightFlowThresholdLph: siteEditForm.leakNightFlowThresholdLph === '' ? null : Number(siteEditForm.leakNightFlowThresholdLph),
        leakNightContinuousHours: siteEditForm.leakNightContinuousHours === '' ? null : Number(siteEditForm.leakNightContinuousHours),
        leakNightStartHour: siteEditForm.leakNightStartHour === '' ? null : Number(siteEditForm.leakNightStartHour),
        leakNightEndHour: siteEditForm.leakNightEndHour === '' ? null : Number(siteEditForm.leakNightEndHour),
        signalStrengthThresholdReadings: siteEditForm.signalStrengthThresholdReadings === '' ? null : Number(siteEditForm.signalStrengthThresholdReadings)
      };
      const updated = await siteService.updateSite(siteId, payload);
      setSite(updated);
      setSiteEditOpen(false);
      setSiteActionSnackbar({ open: true, message: 'Site updated successfully', severity: 'success' });
    } catch (e) {
      console.error('Error updating site:', e);
      setSiteActionSnackbar({ open: true, message: e?.message || 'Failed to update site', severity: 'error' });
    } finally {
      setSiteEditSaving(false);
    }
  }, [siteId, siteEditForm]);

  const handleSiteDeleteConfirm = useCallback(async () => {
    if (!siteId || !isAdmin) return;
    try {
      await siteService.deleteSite(siteId);
      navigate('/sites');
    } catch (e) {
      console.error('Error deleting site:', e);
      setSiteActionSnackbar({ open: true, message: e?.message || 'Failed to delete site', severity: 'error' });
    } finally {
      setSiteDeleteOpen(false);
    }
  }, [siteId, navigate, isAdmin]);

  useEffect(() => {
    if (didRestoreRef.current) return;
    if (!restorePayload) return;

    didRestoreRef.current = true;

    if (restorePayload.usageSubTab !== undefined) {
      setUsageSubTab(restorePayload.usageSubTab || 'water');
    }
    if (restorePayload.connectedDeviceSearch !== undefined) {
      setConnectedDeviceSearch(restorePayload.connectedDeviceSearch || '');
    }
    if (restorePayload.siteNotificationsType !== undefined) {
      setSiteNotificationsType(restorePayload.siteNotificationsType || '');
    }
    if (restorePayload.siteNotificationsStatus !== undefined) {
      setSiteNotificationsStatus(restorePayload.siteNotificationsStatus || '');
    }

    if (restorePayload.activeTab !== undefined) {
      const nextTab = Number(restorePayload.activeTab) || 0;
      setActiveTab([0, 1, 2].includes(nextTab) ? nextTab : 0);
    }

    if (typeof restoreScrollY === 'number') {
      setTimeout(() => window.scrollTo(0, restoreScrollY), 0);
    }
  }, [restorePayload, restoreScrollY]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleUsageSubTabChange = useCallback((event, newTab) => {
    if (newTab !== null) {
      setUsageSubTab(newTab);
    }
  }, []);

  // Process device hierarchy data before passing to ReactFlow component
  const processDeviceHierarchy = useCallback((hierarchy) => {
    if (!hierarchy || hierarchy.length === 0) return [];
    const isElectrical = usageSubTab === 'electrical';
    const predicate = (d) => isElectrical ? d?.deviceType === 'electrical_meter' : d?.deviceType !== 'electrical_meter';
    const filterNode = (node) => {
      if (!predicate(node)) return null;
      const filteredChildren = Array.isArray(node.children)
        ? node.children.map(filterNode).filter(Boolean)
        : [];
      return { ...node, children: filteredChildren };
    };
    return hierarchy.map(filterNode).filter(Boolean);
  }, [usageSubTab]);

  const networkHierarchy = useMemo(() => processDeviceHierarchy(deviceHierarchy), [processDeviceHierarchy, deviceHierarchy]);
  const networkDevices = useMemo(() => {
    const isElectrical = usageSubTab === 'electrical';
    return (devices || []).filter((d) => isElectrical ? d?.deviceType === 'electrical_meter' : d?.deviceType !== 'electrical_meter');
  }, [devices, usageSubTab]);
  const configuredNetworkBulkMeters = useMemo(() => {
    if (!siteId) return [];
    return (networkDevices || []).filter((d) => isDeviceBulkMeterForSite(d, siteId));
  }, [networkDevices, siteId]);
  const inferredNetworkBulkMeter = useMemo(() => findFirstHierarchyNodeWithChildren(networkHierarchy), [networkHierarchy]);

  useEffect(() => {
    const validIds = new Set((networkDevices || []).map((d) => String(d.id)));
    const configuredIds = Array.from(new Set((configuredNetworkBulkMeters || []).map((d) => String(d.id)).filter((id) => validIds.has(id))));
    const inferredId = inferredNetworkBulkMeter?.id !== undefined && inferredNetworkBulkMeter?.id !== null
      ? String(inferredNetworkBulkMeter.id)
      : '';

    setNetworkBulkMeterIds((prev) => {
      const prevList = Array.isArray(prev) ? prev : [];
      const prevValid = Array.from(new Set(prevList.map((id) => String(id)).filter((id) => validIds.has(id))));
      if (prevValid.length > 0) return prevValid;
      if (configuredIds.length > 0) return configuredIds;
      if (inferredId && validIds.has(inferredId)) {
        return [inferredId];
      }
      return [];
    });
  }, [networkDevices, configuredNetworkBulkMeters, inferredNetworkBulkMeter]);

  const loadMonitorData = useCallback(async () => {
    if (!siteId) return;
    const monitorLoadStartMs = Date.now();
    const nowBucket = Math.floor(Date.now() / (5 * 60000));
    const loadKey = `${siteId}:${usageSubTab}:${dashboardData.todayDate || ''}:${(devices || []).length}:${(deviceHierarchy || []).length}:${nowBucket}`;
    if (monitorLoadLastKeyRef.current === loadKey) return;
    monitorLoadLastKeyRef.current = loadKey;
    setMonitorLoading(true);
    setMonitorError(null);
    try {
      const isElectrical = usageSubTab === 'electrical';
      const predicate = (d) => isElectrical ? d?.deviceType === 'electrical_meter' : d?.deviceType !== 'electrical_meter';
      const deviceList = (devices || []).filter(predicate);
      const deviceIds = deviceList.map((d) => d.id);

      const localTodayStr = format(new Date(), 'yyyy-MM-dd');
      const endDateStrCandidate = dashboardData.todayDate || localTodayStr;
      const endDateStr = endDateStrCandidate > localTodayStr ? endDateStrCandidate : localTodayStr;
      const endDate = new Date(endDateStr);
      const startDateAll = subDays(endDate, monitorWindowDays);
      const startDateAllStr = format(startDateAll, 'yyyy-MM-dd');
      const dateLabelsAll = Array.from({ length: monitorWindowDays + 1 }, (_, idx) => format(addDays(startDateAll, idx), 'yyyy-MM-dd'));

      if (deviceIds.length === 0) {
        const dateLabels = dateLabelsAll.slice(1);
        setMonitorDates(dateLabels);
        setMonitorTotalSeries(dateLabels.map(() => 0));
        setMonitorBulkSeries(null);
        setMonitorBulkBreakdown([]);
        setMonitorTopDeviceLabel('');
        setMonitorTopDeviceTotal(null);
        setMonitorLastDayTopDeviceLabel('');
        setMonitorLastDayTopDeviceTotal(null);
        setMonitorLastDayTopConsumers([]);
        setMonitorLastDayUsageByDeviceId({});
        setMonitorTopConsumers([]);
        setMonitorLastActiveDateKey(null);
        setMonitorDataUpdatedAt(new Date());
        return;
      }

      const configuredBulkDeviceIds = (deviceList || [])
        .filter((d) => isDeviceBulkMeterForSite(d, siteId))
        .map((d) => String(d.id));

      const inferredBulkCandidate = findFirstHierarchyNodeWithChildren(deviceHierarchy, predicate);
      const inferredBulkId = inferredBulkCandidate?.id !== undefined && inferredBulkCandidate?.id !== null
        ? String(inferredBulkCandidate.id)
        : '';

      const bulkDeviceIds = configuredBulkDeviceIds.length > 0
        ? Array.from(new Set(configuredBulkDeviceIds))
        : (inferredBulkId ? [inferredBulkId] : []);
      const bulkDeviceIdSet = new Set(bulkDeviceIds);

      if (bulkDeviceIds.length > 0) {
        console.log('Site monitor bulk meters:', {
          deviceIds: bulkDeviceIds,
          count: bulkDeviceIds.length,
          inferredFallback: configuredBulkDeviceIds.length === 0
        });
      }

      const sumDeviceIdSet = new Set(deviceIds.map((id) => String(id)));
      // If bulk meters are part of the site's device list, exclude them from the summed total,
      // so the comparison line stays meaningful.
      bulkDeviceIds.forEach((id) => sumDeviceIdSet.delete(String(id)));

      const requestedIds = Array.from(new Set([
        ...deviceIds.map((id) => String(id)),
        ...bulkDeviceIds.map((id) => String(id))
      ]));

      const bulkUsageFetchStartMs = Date.now();
      const resp = await deviceService.getDevicesDailyUsageBulk({
        deviceIds: requestedIds,
        startDate: startDateAllStr,
        endDate: endDateStr,
        includeLiveToday: true,
        skipReconciliation: true,
        // Monitor bars must match the device pages, so always aggregate from raw readings.
        realtime: true
      });
      console.log('Monitor bulk usage fetch timing:', {
        siteId,
        meterType: usageSubTab,
        requestedDeviceCount: requestedIds.length,
        elapsedMs: Date.now() - bulkUsageFetchStartMs
      });

      const byDevice = new Map((resp?.devices || []).map((d) => [String(d.deviceId), d.daily || []]));

      const getRowDateKey = (rawDate) => {
        const parsedDate = rawDate ? new Date(rawDate) : null;
        return typeof rawDate === 'string'
          ? String(rawDate).slice(0, 10)
          : (parsedDate && !isNaN(parsedDate.getTime()) ? format(parsedDate, 'yyyy-MM-dd') : '');
      };

      let hasEndDateData = false;
      for (const id of requestedIds) {
        const dailyList = byDevice.get(String(id)) || [];
        for (const row of dailyList || []) {
          const dateKey = getRowDateKey(row?.date);
          if (dateKey !== endDateStr) continue;
          const readingCount = parseInt(row?.readingCount, 10) || 0;
          const totalUsage = Number(row?.totalUsage) || 0;
          if (readingCount > 0 || totalUsage !== 0) {
            hasEndDateData = true;
            break;
          }
        }
        if (hasEndDateData) break;
      }

      const dateLabels = hasEndDateData ? dateLabelsAll.slice(1) : dateLabelsAll.slice(0, monitorWindowDays);
      const totalsByDate = new Map(dateLabels.map((d) => [d, 0]));

      const missingBulkIds = bulkDeviceIds.filter((bulkId) => !byDevice.has(String(bulkId)));
      if (missingBulkIds.length > 0) {
        console.warn('Bulk meter daily usage missing from bulk usage endpoint:', {
          siteId,
          missingBulkIds
        });
      }

      const byId = new Map((deviceList || []).map((d) => [String(d.id), d]));

      // Site totals per day (from sum devices), used to pick the last active day
      for (const sumId of sumDeviceIdSet) {
        const dailyList = byDevice.get(String(sumId)) || [];
        (dailyList || []).forEach((row) => {
          const rawDate = row?.date;
          const dateKey = getRowDateKey(rawDate);
          if (!totalsByDate.has(dateKey)) return;
          totalsByDate.set(dateKey, (totalsByDate.get(dateKey) || 0) + (Number(row.totalUsage) || 0));
        });
      }

      let bulkByDate = null;
      let bulkSeries = null;
      if (bulkDeviceIds.length > 0) {
        bulkByDate = new Map(dateLabels.map((d) => [d, 0]));
        bulkDeviceIds.forEach((bulkId) => {
          const bulkDaily = byDevice.get(String(bulkId)) || [];
          (bulkDaily || []).forEach((row) => {
            const rawDate = row?.date;
            const dateKey = getRowDateKey(rawDate);
            if (!bulkByDate.has(dateKey)) return;
            bulkByDate.set(
              dateKey,
              (Number(bulkByDate.get(dateKey) || 0) + (Number(row.totalUsage) || 0))
            );
          });
        });
        bulkSeries = dateLabels.map((d) => Number((bulkByDate.get(d) || 0).toFixed(3)));
      }

      let bulkBreakdown = [];
      if (bulkDeviceIds.length > 1) {
        const parentByDeviceId = new Map((deviceList || []).map((d) => [
          String(d.id),
          d?.parentId !== undefined && d?.parentId !== null ? String(d.parentId) : ''
        ]));

        const resolveOwningBulkId = (deviceId) => {
          const id = String(deviceId || '');
          if (!id) return '';
          if (bulkDeviceIdSet.has(id)) return id;

          let current = id;
          const visited = new Set();
          while (current && !visited.has(current)) {
            visited.add(current);
            const parentId = parentByDeviceId.get(current) || '';
            if (!parentId) return '';
            if (bulkDeviceIdSet.has(parentId)) return parentId;
            current = parentId;
          }
          return '';
        };

        const baseByBulkByDate = new Map(
          bulkDeviceIds.map((bulkId) => [String(bulkId), new Map(dateLabels.map((d) => [d, 0]))])
        );

        for (const sumId of sumDeviceIdSet) {
          const ownerBulkId = resolveOwningBulkId(sumId);
          if (!ownerBulkId || !baseByBulkByDate.has(ownerBulkId)) continue;
          const ownedBaseByDate = baseByBulkByDate.get(ownerBulkId);
          const dailyList = byDevice.get(String(sumId)) || [];
          (dailyList || []).forEach((row) => {
            const dateKey = getRowDateKey(row?.date);
            if (!ownedBaseByDate.has(dateKey)) return;
            ownedBaseByDate.set(
              dateKey,
              (Number(ownedBaseByDate.get(dateKey) || 0) + (Number(row?.totalUsage) || 0))
            );
          });
        }

        bulkBreakdown = bulkDeviceIds.map((bulkId) => {
          const bulkDaily = byDevice.get(String(bulkId)) || [];
          const bulkByDateSingle = new Map(dateLabels.map((d) => [d, 0]));
          (bulkDaily || []).forEach((row) => {
            const dateKey = getRowDateKey(row?.date);
            if (!bulkByDateSingle.has(dateKey)) return;
            bulkByDateSingle.set(dateKey, Number(bulkByDateSingle.get(dateKey) || 0) + (Number(row?.totalUsage) || 0));
          });

          const baseByDate = baseByBulkByDate.get(String(bulkId)) || new Map(dateLabels.map((d) => [d, 0]));
          const meter = byId.get(String(bulkId));
          const label = meter?.description || meter?.serialNumber || String(bulkId);

          return {
            bulkId: String(bulkId),
            label: String(label),
            serialNumber: meter?.serialNumber || '',
            baseSeries: dateLabels.map((d) => Number((baseByDate.get(d) || 0).toFixed(3))),
            bulkSeries: dateLabels.map((d) => Number((bulkByDateSingle.get(d) || 0).toFixed(3)))
          };
        });
      }

      // Some meters report a day in arrears, so the latest calendar day can be empty.
      // Use the most recent day in range that actually has usage (site total or bulk).
      const lastDateKey = dateLabels[dateLabels.length - 1] || endDateStr;
      let lastActiveDateKey = lastDateKey;
      for (let i = dateLabels.length - 1; i >= 0; i--) {
        const d = dateLabels[i];
        const siteVal = Number(totalsByDate.get(d) || 0);
        const bulkVal = bulkByDate ? Number(bulkByDate.get(d) || 0) : 0;
        if (siteVal > 0 || bulkVal > 0) {
          lastActiveDateKey = d;
          break;
        }
      }
      setMonitorLastActiveDateKey(lastActiveDateKey);

      // Highest-usage device across the visible window (exclude bulk meter)
      let topDeviceId = null;
      let topDeviceTotal = -1;
      let topDeviceLastDayId = null;
      let topDeviceLastDayTotal = -1;
      const lastDayUsageByDevice = [];
      const usage14dByDevice = [];
      for (const id of deviceIds) {
        if (bulkDeviceIdSet.has(String(id))) continue;
        const dailyList = byDevice.get(String(id)) || [];
        const total = (dailyList || []).reduce((sum, row) => {
          const rawDate = row?.date;
          const dateKey = getRowDateKey(rawDate);
          if (!totalsByDate.has(dateKey)) return sum;
          return sum + (Number(row.totalUsage) || 0);
        }, 0);
        if (total > topDeviceTotal) {
          topDeviceTotal = total;
          topDeviceId = id;
        }
        usage14dByDevice.push({ id, total });

        const dayTotal = (dailyList || []).reduce((sum, row) => {
          const rawDate = row?.date;
          const dateKey = getRowDateKey(rawDate);
          if (dateKey !== lastActiveDateKey) return sum;
          return sum + (Number(row.totalUsage) || 0);
        }, 0);
        if (dayTotal > topDeviceLastDayTotal) {
          topDeviceLastDayTotal = dayTotal;
          topDeviceLastDayId = id;
        }

        lastDayUsageByDevice.push({ id, total: dayTotal });
      }
      if (topDeviceId && topDeviceTotal >= 0) {
        const d = byId.get(String(topDeviceId));
        const label = d?.description || d?.serialNumber || String(topDeviceId);
        setMonitorTopDeviceLabel(String(label));
        setMonitorTopDeviceTotal(Number(topDeviceTotal.toFixed(3)));
      } else {
        setMonitorTopDeviceLabel('');
        setMonitorTopDeviceTotal(null);
      }

      if (topDeviceLastDayId && topDeviceLastDayTotal >= 0) {
        const d = byId.get(String(topDeviceLastDayId));
        const label = d?.description || d?.serialNumber || String(topDeviceLastDayId);
        setMonitorLastDayTopDeviceLabel(String(label));
        setMonitorLastDayTopDeviceTotal(Number(topDeviceLastDayTotal.toFixed(3)));
      } else {
        setMonitorLastDayTopDeviceLabel('');
        setMonitorLastDayTopDeviceTotal(null);
      }

      const usageMap = {};
      lastDayUsageByDevice.forEach((c) => {
        usageMap[String(c.id)] = Number(c.total) || 0;
      });
      setMonitorLastDayUsageByDeviceId(usageMap);

      const topConsumers = lastDayUsageByDevice
        .filter((c) => Number(c.total) > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10)
        .map((c) => {
          const d = byId.get(String(c.id));
          return {
            id: c.id,
            serialNumber: d?.serialNumber || 'N/A',
            description: d?.description || '-',
            total: Number(Number(c.total).toFixed(3))
          };
        });
      setMonitorLastDayTopConsumers(topConsumers);

      const topConsumers14d = usage14dByDevice
        .filter((c) => Number(c.total) > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10)
        .map((c) => {
          const d = byId.get(String(c.id));
          return {
            id: c.id,
            serialNumber: d?.serialNumber || 'N/A',
            description: d?.description || '-',
            total: Number(Number(c.total).toFixed(3))
          };
        });
      setMonitorTopConsumers(topConsumers14d);

      const totals = dateLabels.map((d) => Number(((totalsByDate.get(d) || 0)).toFixed(3)));
      setMonitorDates(dateLabels);
      setMonitorTotalSeries(totals);
      setMonitorBulkSeries(bulkSeries);
      setMonitorBulkBreakdown(bulkBreakdown);
      setMonitorDataUpdatedAt(new Date());

      console.log('Monitor usage load timing:', {
        siteId,
        meterType: usageSubTab,
        deviceCount: deviceIds.length,
        dateCount: dateLabels.length,
        elapsedMs: Date.now() - monitorLoadStartMs
      });
    } catch (e) {
      console.error('Error loading monitor data:', e);
      setMonitorError('Failed to load monitor data');
      setMonitorDates([]);
      setMonitorTotalSeries([]);
      setMonitorBulkSeries(null);
      setMonitorBulkBreakdown([]);
      setMonitorLastDayTopDeviceLabel('');
      setMonitorLastDayTopDeviceTotal(null);
      setMonitorLastDayTopConsumers([]);
      setMonitorLastDayUsageByDeviceId({});
      setMonitorTopConsumers([]);
      setMonitorLastActiveDateKey(null);
      setMonitorDataUpdatedAt(null);
    } finally {
      setMonitorLoading(false);
    }
  }, [siteId, devices, usageSubTab, dashboardData.todayDate, deviceHierarchy, monitorWindowDays]);

  useEffect(() => {
    if (activeTab === 0) {
      loadMonitorData();
    }
  }, [activeTab, loadMonitorData]);

  useEffect(() => {
    if (!siteId) return;
    if (activeTab !== 0) return;
    if (monitorLoading) return;
    if (monitorShowLast14Days) return;
    if (!Array.isArray(monitorDates) || monitorDates.length === 0) return;

    const expandKey = `${siteId}:${usageSubTab}`;
    if (monitorAutoExpandLast14Ref.current === expandKey) return;

    monitorAutoExpandLast14Ref.current = expandKey;
    setMonitorShowLast14Days(true);
  }, [siteId, activeTab, usageSubTab, monitorLoading, monitorDates, monitorShowLast14Days]);

  const loadMonitorNotificationSummary = useCallback(async () => {
    if (!siteId) return;
    const notifSummaryLoadStartMs = Date.now();
    const nowBucket = Math.floor(Date.now() / (5 * 60000));
    const loadKey = `${siteId}:${dashboardData.todayDate || ''}:${monitorLastActiveDateKey || ''}:${nowBucket}`;
    if (monitorNotifLoadLastKeyRef.current === loadKey) return;
    monitorNotifLoadLastKeyRef.current = loadKey;
    setMonitorNotifSummaryLoading(true);
    try {
      const canUseMonitorDates = Array.isArray(monitorDates)
        && monitorDates.length === monitorWindowDays
        && monitorDates.every((d) => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d));

      const localTodayStr = format(new Date(), 'yyyy-MM-dd');
      const endDateStrCandidate = dashboardData.todayDate || localTodayStr;
      const endDateStr = endDateStrCandidate > localTodayStr ? endDateStrCandidate : localTodayStr;
      const endDate = new Date(endDateStr);
      const startDate = subDays(endDate, monitorWindowDays - 1);
      const dateLabels = canUseMonitorDates
        ? monitorDates
        : Array.from({ length: monitorWindowDays }, (_, idx) => format(addDays(startDate, idx), 'yyyy-MM-dd'));
      const startDateLabel = dateLabels[0];

      const getNotificationDateKey = (n) => {
        const dataDate = n?.data?.date;
        if (typeof dataDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dataDate)) {
          return dataDate;
        }
        const dt = n?.detectedAt || n?.createdAt;
        if (!dt) return null;
        const parsed = new Date(dt);
        if (isNaN(parsed.getTime())) return null;
        return format(parsed, 'yyyy-MM-dd');
      };

      // Only fetch notifications from (just before) the visible window. Leaks are detected
      // up to a day after their analysed date, so pad the lower bound by 2 days.
      const fetchStartDate = (() => {
        try {
          return format(subDays(new Date(startDateLabel), 2), 'yyyy-MM-dd');
        } catch {
          return undefined;
        }
      })();

      const fetchNotificationsWindow = async () => {
        const collected = [];
        const limit = 1000;
        let offset = 0;
        let page = 0;
        const maxPages = 5;

        const requestedTypes = ['leak_detected', 'high_usage', 'signal_strength', 'threshold_exceeded'];

        while (page < maxPages) {
          const resp = await siteService.fetchSiteNotifications(siteId, {
            limit,
            offset,
            types: requestedTypes,
            includeTotal: false,
            startDate: fetchStartDate,
            endDate: endDateStr
          });
          const rawList = resp?.notifications ?? resp?.rows ?? resp;
          const list = Array.isArray(rawList) ? rawList : [];
          if (list.length === 0) break;
          collected.push(...list);

          let minDateLabel = null;
          (list || []).forEach((n) => {
            const dk = getNotificationDateKey(n);
            if (!dk) return;
            if (!minDateLabel || dk < minDateLabel) minDateLabel = dk;
          });
          if (minDateLabel && minDateLabel < startDateLabel) break;

          offset += list.length;
          const total = Number(resp?.total);
          if (Number.isFinite(total) && offset >= total) break;
          if (list.length < limit) break;

          page += 1;
        }

        return collected;
      };

      const list = await fetchNotificationsWindow();

      const pendingByDeviceId = {};
      const pendingTypeByDeviceId = {};
      (list || []).forEach((n) => {
        const status = (n?.status || '').toString().toLowerCase();
        if (status === 'resolved') return;
        const deviceId = n?.deviceId ?? n?.device?.id;
        if (deviceId === undefined || deviceId === null) return;
        const key = String(deviceId);
        const type = String(n?.type || 'unknown');
        pendingByDeviceId[key] = (pendingByDeviceId[key] || 0) + 1;
        if (!pendingTypeByDeviceId[key]) {
          pendingTypeByDeviceId[key] = {};
        }
        pendingTypeByDeviceId[key][type] = (pendingTypeByDeviceId[key][type] || 0) + 1;
      });
      setPendingNotificationCountByDeviceId(pendingByDeviceId);
      setPendingNotificationTypeCountByDeviceId(pendingTypeByDeviceId);

      const notifCounts = new Map(dateLabels.map((d) => [d, 0]));
      const lastDateLabel = (monitorLastActiveDateKey && dateLabels.includes(monitorLastActiveDateKey))
        ? monitorLastActiveDateKey
        : (dateLabels[dateLabels.length - 1] || null);
      const allowedTypes = new Set([
        'leak_detected',
        'high_usage',
        'signal_strength',
        'threshold_exceeded'
      ]);
      const leakTypes = new Set(['leak_detected', 'high_usage']);
      let leak14d = 0;
      let lowSignal14d = 0;
      let threshold14d = 0;
      let leak1d = 0;
      let lowSignal1d = 0;
      let threshold1d = 0;
      (list || []).forEach((n) => {
        const t = String(n?.type || '');
        if (!allowedTypes.has(t)) return;
        const status = (n?.status || '').toString().toLowerCase();
        if (status === 'resolved') return;

        const dateKey = getNotificationDateKey(n);
        if (!dateKey) return;
        if (!notifCounts.has(dateKey)) return;
        notifCounts.set(dateKey, (notifCounts.get(dateKey) || 0) + 1);

        if (leakTypes.has(t)) leak14d += 1;
        if (t === 'signal_strength') lowSignal14d += 1;
        if (t === 'threshold_exceeded') threshold14d += 1;

        if (lastDateLabel && dateKey === lastDateLabel) {
          if (leakTypes.has(t)) leak1d += 1;
          if (t === 'signal_strength') lowSignal1d += 1;
          if (t === 'threshold_exceeded') threshold1d += 1;
        }
      });
      const countsList = dateLabels.map((d) => notifCounts.get(d) || 0);
      setMonitorNotificationCountsByDate(countsList);

      setMonitorActiveLeaks(leak14d);
      setMonitorActiveLowSignal(lowSignal14d);
      setMonitorActiveThresholds(threshold14d);
      setMonitorLastDayLeaks(leak1d);
      setMonitorLastDayLowSignal(lowSignal1d);
      setMonitorLastDayThresholds(threshold1d);

      const notificationsInRange = (list || []).filter((n) => {
        const t = String(n?.type || '');
        if (!allowedTypes.has(t)) return false;
        const dateKey = getNotificationDateKey(n);
        return Boolean(dateKey && notifCounts.has(dateKey));
      });

      const getNotificationSortTs = (n) => {
        const raw = n?.detectedAt || n?.createdAt || n?.data?.date;
        const parsed = raw ? new Date(raw) : null;
        return parsed && !isNaN(parsed.getTime()) ? parsed.getTime() : 0;
      };

      const sortedNotifications = [...notificationsInRange].sort((a, b) => getNotificationSortTs(b) - getNotificationSortTs(a));
      const lastDayNotifications = lastDateLabel
        ? sortedNotifications.filter((n) => getNotificationDateKey(n) === lastDateLabel)
        : [];

      setMonitorLast14DayNotifications(sortedNotifications);
      setMonitorLastDayNotifications(lastDayNotifications);

      console.log('Monitor notification summary timing:', {
        siteId,
        notificationsFetched: list.length,
        dateCount: dateLabels.length,
        elapsedMs: Date.now() - notifSummaryLoadStartMs
      });
    } catch (e) {
      console.error('Error loading monitor notification summary:', e);
      setMonitorNotificationCountsByDate([]);
      setPendingNotificationCountByDeviceId({});
      setMonitorActiveLeaks(0);
      setMonitorActiveLowSignal(0);
      setMonitorActiveThresholds(0);
      setMonitorLastDayLeaks(0);
      setMonitorLastDayLowSignal(0);
      setMonitorLastDayThresholds(0);
      setPendingNotificationTypeCountByDeviceId({});
      setMonitorLast14DayNotifications([]);
      setMonitorLastDayNotifications([]);
    } finally {
      setMonitorNotifSummaryLoading(false);
    }
  }, [siteId, dashboardData.todayDate, monitorDates, monitorLastActiveDateKey, monitorWindowDays]);

  useEffect(() => {
    if (activeTab === 0 && monitorShowLast14Days) {
      loadMonitorNotificationSummary();
    }
  }, [activeTab, monitorShowLast14Days, loadMonitorNotificationSummary]);

  useEffect(() => {
    if (!siteId) return;
    if (activeTab !== 0) return;
    const intervalId = setInterval(() => {
      loadMonitorData();
      if (monitorShowLast14Days) {
        loadMonitorNotificationSummary();
      }
    }, 5 * 60000);
    return () => clearInterval(intervalId);
  }, [siteId, activeTab, monitorShowLast14Days, loadMonitorData, loadMonitorNotificationSummary]);

  useEffect(() => {
    if (!siteId) return;
    if (activeTab !== 0) return;
    if (monitorLoading || monitorNotifSummaryLoading) return;

    const total7d = (monitorTotalSeries || []).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const bulk7d = monitorBulkSeries
      ? (monitorBulkSeries || []).reduce((sum, v) => sum + (Number(v) || 0), 0)
      : null;

    const notificationEvents = (monitorDates || []).map((d, idx) => ({
      date: d,
      count: Number(monitorNotificationCountsByDate?.[idx] || 0)
    })).filter((x) => x.count > 0);

    const logKey = `${siteId}:${usageSubTab}`;
    if (monitorSummaryLastLoggedRef.current === logKey) return;
    monitorSummaryLastLoggedRef.current = logKey;

    console.log('Site monitor summary (7d):', {
      siteId,
      meterType: usageSubTab,
      siteTotal7d: total7d,
      bulkTotal7d: bulk7d,
      topDeviceLabel: monitorTopDeviceLabel,
      topDeviceTotal7d: monitorTopDeviceTotal,
      activeLeaks: monitorActiveLeaks,
      activeLowSignal: monitorActiveLowSignal,
      activeThresholds: monitorActiveThresholds,
      notificationEvents
    });
  }, [
    siteId,
    activeTab,
    usageSubTab,
    monitorLoading,
    monitorNotifSummaryLoading,
    monitorTotalSeries,
    monitorBulkSeries,
    monitorTopDeviceLabel,
    monitorTopDeviceTotal,
    monitorActiveLeaks,
    monitorActiveLowSignal,
    monitorActiveThresholds,
    monitorDates,
    monitorNotificationCountsByDate
  ]);

  const loadSiteNotifications = useCallback(async () => {
    if (!siteId) return;
    setSiteNotificationsLoading(true);
    setSiteNotificationsError(null);
    try {
      // Scope to the last 2 days so type/status filters surface recent alerts
      const windowStart = new Date();
      windowStart.setHours(0, 0, 0, 0);
      windowStart.setDate(windowStart.getDate() - 1);
      const resp = await siteService.fetchSiteNotifications(siteId, {
        limit: 1000,
        offset: 0,
        type: siteNotificationsType || undefined,
        status: siteNotificationsStatus || undefined,
        startDate: windowStart.toISOString()
      });
      const rawList = resp?.notifications ?? resp?.rows ?? resp;
      const list = Array.isArray(rawList) ? rawList : [];
      console.log('Site notifications loaded:', { count: list.length });
      if (!Array.isArray(rawList) && !Array.isArray(resp?.notifications) && !Array.isArray(resp?.rows)) {
        console.warn('Unexpected notifications response shape:', resp);
      }
      setSiteNotifications(list);
    } catch (e) {
      console.error('Error loading site notifications:', e);
      setSiteNotificationsError('Failed to load notifications');
      setSiteNotifications([]);
    } finally {
      setSiteNotificationsLoading(false);
    }
  }, [siteId, siteNotificationsType, siteNotificationsStatus]);

  useEffect(() => {
    if (activeTab === 1) {
      loadSiteNotifications();
    }
  }, [activeTab, loadSiteNotifications]);

  const handleSiteNotificationUpdateStatus = useCallback(async (notificationId, status) => {
    if (!siteId) return;
    try {
      setSiteNotificationsError(null);
      const updated = await siteService.updateSiteNotificationStatus(siteId, notificationId, status);
      setSiteNotifications((prev) => (prev || []).map((n) => (n.id === notificationId ? updated : n)));

      setMonitorLast14DayNotifications((prev) => (prev || []).map((n) => (n.id === notificationId ? updated : n)));
      setMonitorLastDayNotifications((prev) => (prev || []).map((n) => (n.id === notificationId ? updated : n)));

      monitorNotifLoadLastKeyRef.current = '';
      loadMonitorNotificationSummary();
    } catch (e) {
      console.error('Error updating site notification status:', e);
      setSiteNotificationsError('Failed to update notification status');
    }
  }, [siteId, loadMonitorNotificationSummary]);

  const handleSiteNotificationDelete = useCallback(async (notificationId) => {
    if (!siteId) return;
    try {
      setSiteNotificationsError(null);
      await siteService.deleteSiteNotification(siteId, notificationId);
      setSiteNotifications((prev) => (prev || []).filter((n) => n.id !== notificationId));

      setMonitorLast14DayNotifications((prev) => (prev || []).filter((n) => n.id !== notificationId));
      setMonitorLastDayNotifications((prev) => (prev || []).filter((n) => n.id !== notificationId));

      monitorNotifLoadLastKeyRef.current = '';
      loadMonitorNotificationSummary();
    } catch (e) {
      console.error('Error deleting site notification:', e);
      setSiteNotificationsError('Failed to delete notification');
    }
  }, [siteId, loadMonitorNotificationSummary]);

  const handleExportSiteDevicesCSV = useCallback(async () => {
    if (!siteId) return;
    try {
      setSiteCsvExporting(true);
      await downloadSiteDevicesCSV(siteId, {
        search: connectedDeviceSearch || '',
        deviceScope: usageSubTab === 'electrical' ? 'electrical' : 'water'
      });
    } catch (error) {
      console.error('Error exporting site devices CSV:', error);
      setSiteActionSnackbar({ open: true, message: 'Failed to export site CSV', severity: 'error' });
    } finally {
      setSiteCsvExporting(false);
    }
  }, [siteId, connectedDeviceSearch, usageSubTab]);

  // Handle back button click
  const handleBackClick = useCallback(() => {
    navigate("/sites");
  }, [navigate]);

  // Build device hierarchy from device list
  const buildDeviceHierarchy = useCallback((devicesList, externalParentsList = []) => {
    if (!devicesList || devicesList.length === 0) {
      setDeviceHierarchy([]);
      return;
    }

    try {
      const deviceMap = new Map();
      devicesList.forEach((device) => {
        deviceMap.set(device.id.toString(), { ...device, children: [] });
      });

      externalParentsList.forEach((device) => {
        const key = device?.id !== undefined && device?.id !== null ? device.id.toString() : null;
        if (!key) return;
        if (deviceMap.has(key)) return;
        deviceMap.set(key, { ...device, isExternal: true, parentId: null, children: [] });
      });

      const rootDeviceMap = new Map();
      const ensureRoot = (node) => {
        if (!node || node.id === undefined || node.id === null) return;
        const key = node.id.toString();
        if (!rootDeviceMap.has(key)) rootDeviceMap.set(key, node);
      };

      devicesList.forEach((device) => {
        const deviceWithChildren = deviceMap.get(device.id.toString());
        if (device.parentId) {
          const parent = deviceMap.get(device.parentId.toString());
          if (parent) {
            parent.children.push(deviceWithChildren);

            // If the parent is external to this site, show it as a single top-level "ghost" node.
            if (parent.isExternal) {
              ensureRoot(parent);
            }
          } else {
            ensureRoot(deviceWithChildren);
          }
        } else {
          ensureRoot(deviceWithChildren);
        }
      });

      setDeviceHierarchy(Array.from(rootDeviceMap.values()));
    } catch (error) {
      console.error("Error building device hierarchy:", error);
    }
  }, []);

  const loadSiteData = useCallback(async () => {
    setLoading(true);
    try {
      const sitesData = await siteService.fetchSites();
      const currentSite = sitesData.find((s) => s.id.toString() === siteId);

      if (!currentSite) {
        console.error(`Site with ID ${siteId} not found`);
        setLoading(false);
        return;
      }

      setSite(currentSite);

      const data = await dashboardService.getDashboardData(siteId);
      setDashboardData({ todayDate: data.todayDate || new Date().toISOString().split('T')[0] });

      try {
        const siteDevices = await deviceService.fetchDevicesBySite(siteId);
        setDevices(siteDevices);

        const siteDeviceIdSet = new Set((siteDevices || []).map((d) => d?.id !== undefined && d?.id !== null ? d.id.toString() : ''));
        const missingParentTypeById = new Map();
        (siteDevices || []).forEach((d) => {
          if (!d?.parentId) return;
          const parentKey = d.parentId.toString();
          if (siteDeviceIdSet.has(parentKey)) return;
          if (!missingParentTypeById.has(parentKey)) {
            missingParentTypeById.set(parentKey, d.deviceType);
          }
        });

        const missingParentIds = Array.from(missingParentTypeById.keys());
        let externalParents = [];
        if (missingParentIds.length > 0) {
          externalParents = await Promise.all(
            missingParentIds.map(async (parentId) => {
              try {
                return await deviceService.fetchDevice(parentId);
              } catch (e) {
                return {
                  id: parentId,
                  serialNumber: '',
                  description: 'External Parent',
                  deviceType: missingParentTypeById.get(parentId),
                  isActive: true,
                  parentId: null
                };
              }
            })
          );
        }

        buildDeviceHierarchy(siteDevices, externalParents);
      } catch (deviceError) {
        console.error("Error fetching devices for site:", deviceError);
      }
    } catch (error) {
      console.error("Error loading site data:", error);
    } finally {
      setLoading(false);
    }
  }, [siteId, buildDeviceHierarchy]);

  useEffect(() => {
    if (siteId) {
      loadSiteData();
    }
  }, [siteId, loadSiteData]);

  const formatNetworkDeviceLabel = useCallback((device) => {
    if (!device) return '';
    const serial = device?.serialNumber ? `S/N ${device.serialNumber}` : '';
    const description = device?.description || '';
    const locationText = device?.location || '';
    const main = serial && description ? `${serial} - ${description}` : (description || serial || String(device?.id || 'Device'));
    return locationText ? `${main} (${locationText})` : main;
  }, []);

  const formatNetworkParentOptionLabel = useCallback((device) => {
    if (!device) return '';
    return device?.serialNumber ? `S/N ${device.serialNumber}` : String(device?.id || 'Device');
  }, []);

  const selectedNetworkBulkMeters = useMemo(() => {
    const selectedIdSet = new Set((networkBulkMeterIds || []).map((id) => String(id)));
    return (networkDevices || []).filter((d) => selectedIdSet.has(String(d.id)));
  }, [networkDevices, networkBulkMeterIds]);

  const detectedNetworkBulkMeters = useMemo(() => {
    if ((configuredNetworkBulkMeters || []).length > 0) return configuredNetworkBulkMeters;
    return inferredNetworkBulkMeter ? [inferredNetworkBulkMeter] : [];
  }, [configuredNetworkBulkMeters, inferredNetworkBulkMeter]);

  const networkBulkParentOptions = useMemo(() => {
    const candidateParents = (selectedNetworkBulkMeters || []).length > 0
      ? selectedNetworkBulkMeters
      : detectedNetworkBulkMeters;
    const networkDeviceIdSet = new Set((networkDevices || []).map((d) => String(d.id)));
    const seen = new Set();
    return (candidateParents || []).filter((device) => {
      const id = device?.id !== undefined && device?.id !== null ? String(device.id) : '';
      if (!id) return false;
      if (!networkDeviceIdSet.has(id)) return false;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [selectedNetworkBulkMeters, detectedNetworkBulkMeters, networkDevices]);

  const networkBulkParentOptionIds = useMemo(() => {
    return new Set((networkBulkParentOptions || []).map((d) => String(d.id)));
  }, [networkBulkParentOptions]);

  useEffect(() => {
    setNetworkParentAssignments((prev) => {
      const next = {};
      (networkDevices || []).forEach((device) => {
        const deviceId = device?.id !== undefined && device?.id !== null ? String(device.id) : '';
        if (!deviceId) return;

        const previousValue = prev?.[deviceId];
        if (previousValue !== undefined) {
          const normalizedPrev = previousValue ? String(previousValue) : '';
          if (!normalizedPrev || networkBulkParentOptionIds.has(normalizedPrev)) {
            next[deviceId] = normalizedPrev;
            return;
          }
        }

        const currentParentId = device?.parentId !== undefined && device?.parentId !== null
          ? String(device.parentId)
          : '';
        next[deviceId] = networkBulkParentOptionIds.has(currentParentId) ? currentParentId : '';
      });
      return next;
    });
  }, [networkDevices, networkBulkParentOptionIds]);

  const handleApplyBulkMeter = useCallback(async () => {
    const selectedIds = Array.from(new Set((networkBulkMeterIds || []).map((id) => String(id)).filter(Boolean)));

    if (!siteId || selectedIds.length === 0) {
      setSiteActionSnackbar({ open: true, message: 'Select at least one bulk meter first', severity: 'warning' });
      return;
    }

    if (!canConfigureBulkMeter) {
      setSiteActionSnackbar({ open: true, message: 'You do not have permission to update network hierarchy', severity: 'error' });
      return;
    }

    try {
      setNetworkBulkMeterSaving(true);
      const selectedIdSet = new Set(selectedIds);
      const siteTypeDeviceIdSet = new Set((networkDevices || []).map((d) => String(d.id)));

      const metadataUpdates = [];
      (networkDevices || []).forEach((device) => {
        const deviceId = device?.id !== undefined && device?.id !== null ? String(device.id) : '';
        if (!deviceId) return;
        const currentSiteIds = getBulkMeterSiteIdsFromDevice(device);
        const currentSet = new Set(currentSiteIds.map((id) => String(id)));
        const shouldBeBulk = selectedIdSet.has(deviceId);
        const hasSite = currentSet.has(String(siteId));
        if (shouldBeBulk === hasSite) return;

        if (shouldBeBulk) currentSet.add(String(siteId));
        else currentSet.delete(String(siteId));

        metadataUpdates.push({
          deviceId,
          siteIds: Array.from(currentSet)
        });
      });

      if (metadataUpdates.length > 0) {
        await Promise.all(
          metadataUpdates.map((u) =>
            deviceService.updateDevice(u.deviceId, {
              metadata: {
                [BULK_METER_SITE_IDS_METADATA_KEY]: u.siteIds
              }
            })
          )
        );
      }

      await Promise.all(selectedIds.map((id) => deviceService.updateDevice(id, { parentId: null })));

      const rootIdsToAttach = (networkHierarchy || [])
        .map((node) => (node?.id !== undefined && node?.id !== null ? String(node.id) : ''))
        .filter((id) => id && siteTypeDeviceIdSet.has(id) && !selectedIdSet.has(id));

      if (selectedIds.length === 1 && rootIdsToAttach.length > 0) {
        await deviceService.bulkUpdateDevices({
          deviceIds: rootIdsToAttach,
          parentId: selectedIds[0]
        });
      }

      await loadSiteData();
      setSiteActionSnackbar({
        open: true,
        message: `Saved ${selectedIds.length} bulk meter${selectedIds.length === 1 ? '' : 's'} for ${usageSubTab === 'electrical' ? 'electrical' : 'water'} network`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating bulk meter hierarchy:', error);
      setSiteActionSnackbar({
        open: true,
        message: error?.message || 'Failed to update bulk meter',
        severity: 'error'
      });
    } finally {
      setNetworkBulkMeterSaving(false);
    }
  }, [siteId, networkBulkMeterIds, canConfigureBulkMeter, networkDevices, networkHierarchy, loadSiteData, usageSubTab]);

  const handleNetworkParentAssignmentChange = useCallback((deviceId, parentId) => {
    const key = String(deviceId);
    setNetworkParentAssignments((prev) => ({
      ...prev,
      [key]: parentId ? String(parentId) : ''
    }));
  }, []);

  const handleSaveNetworkParentAssignments = useCallback(async () => {
    if (!siteId) return;

    if (!canConfigureBulkMeter) {
      setSiteActionSnackbar({ open: true, message: 'You do not have permission to update network hierarchy', severity: 'error' });
      return;
    }

    if ((networkBulkParentOptions || []).length === 0) {
      setSiteActionSnackbar({ open: true, message: 'Save at least one bulk meter first', severity: 'warning' });
      return;
    }

    try {
      setNetworkParentSaving(true);

      const updates = [];
      (networkDevices || []).forEach((device) => {
        const deviceId = device?.id !== undefined && device?.id !== null ? String(device.id) : '';
        if (!deviceId) return;
        if (networkBulkParentOptionIds.has(deviceId)) return;

        const selectedParent = networkParentAssignments?.[deviceId];
        const selectedParentId = selectedParent ? String(selectedParent) : '';
        const nextParentId = selectedParentId && networkBulkParentOptionIds.has(selectedParentId)
          ? selectedParentId
          : null;

        const currentParentIdRaw = device?.parentId !== undefined && device?.parentId !== null
          ? String(device.parentId)
          : null;
        const currentParentId = currentParentIdRaw && networkBulkParentOptionIds.has(currentParentIdRaw)
          ? currentParentIdRaw
          : null;

        if (currentParentId === nextParentId) return;

        updates.push({
          deviceId,
          parentId: nextParentId
        });
      });

      if (updates.length === 0) {
        setSiteActionSnackbar({ open: true, message: 'No parent changes to save', severity: 'info' });
        return;
      }

      await Promise.all(updates.map((u) => deviceService.updateDevice(u.deviceId, { parentId: u.parentId })));
      await loadSiteData();

      setSiteActionSnackbar({
        open: true,
        message: `Updated parent mapping for ${updates.length} meter${updates.length === 1 ? '' : 's'}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving network parent assignments:', error);
      setSiteActionSnackbar({
        open: true,
        message: error?.message || 'Failed to save parent assignments',
        severity: 'error'
      });
    } finally {
      setNetworkParentSaving(false);
    }
  }, [siteId, canConfigureBulkMeter, networkBulkParentOptions, networkDevices, networkBulkParentOptionIds, networkParentAssignments, loadSiteData]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!site) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Site not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          Back to Sites
        </Button>
      </Box>
    );
  }

  const waterCount = (devices || []).filter(d => d?.deviceType !== 'electrical_meter').length;
  const electricalCount = (devices || []).filter(d => d?.deviceType === 'electrical_meter').length;
  const monitorLastActiveIndex = (() => {
    const dates = monitorDates || [];
    if (dates.length === 0) return -1;
    if (monitorLastActiveDateKey) {
      const idx = dates.indexOf(monitorLastActiveDateKey);
      if (idx >= 0) return idx;
    }
    return dates.length - 1;
  })();
  const monitorLatestDate = monitorLastActiveIndex >= 0 ? monitorDates[monitorLastActiveIndex] : null;
  const monitorLastDayTotal = monitorLastActiveIndex >= 0 && (monitorTotalSeries || []).length > monitorLastActiveIndex
    ? Number(monitorTotalSeries[monitorLastActiveIndex] || 0)
    : 0;
  const monitorLastDayBulkTotal = Array.isArray(monitorBulkSeries) && monitorLastActiveIndex >= 0 && monitorBulkSeries.length > monitorLastActiveIndex
    ? Number(monitorBulkSeries[monitorLastActiveIndex] || 0)
    : null;
  const monitorBulkBreakdownEntries = (monitorBulkBreakdown || []).map((entry) => {
    const bulk = Number(entry?.bulkSeries?.[monitorLastActiveIndex] || 0);
    const base = Number(entry?.baseSeries?.[monitorLastActiveIndex] || 0);
    return {
      ...entry,
      lastDayBulk: bulk,
      lastDayBase: base,
      lastDayDelta: bulk - base > 0 ? bulk - base : 0
    };
  });

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: 'wrap', gap: 1, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/sites")}
              size={isMobile ? "small" : "medium"}
              sx={{ mr: 2 }}
            >
              Back to Sites
            </Button>
            <Typography 
              variant={isMobile ? "h5" : "h4"} 
              component="h1" 
              sx={{ 
                flexGrow: 1,
                wordBreak: 'break-word',
                mt: { xs: 1, sm: 0 }
              }}
            >
              {site?.description || "Site Details"}
            </Typography>
            {!isEndUser && (
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {(authUser?.role === 'admin' || authUser?.role === 'operator' || authUser?.role === 'super_user') && (
                  <Tooltip title="Export Site CSV">
                    <IconButton size="small" onClick={handleExportSiteDevicesCSV} disabled={siteCsvExporting}>
                      <FileDownload fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit Site">
                  <IconButton size="small" onClick={openEditSiteDialog}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                {isAdmin && (
                  <Tooltip title="Delete Site">
                    <IconButton size="small" color="error" onClick={openDeleteSiteDialog}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardHeader 
                title="Site Overview" 
                titleTypographyProps={{ variant: isMobile ? 'h6' : 'h5' }}
                sx={{ pb: isMobile ? 0.5 : 1 }}
              />
              <Divider />
              <CardContent sx={{ p: { xs: 0.75, sm: 1.5 } }}>
                <Grid container spacing={isMobile ? 0.75 : 1}>
                  <Grid item xs={12} sm={4} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LocationOn sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                      <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Location
                        </Typography>
                        <Typography
                          variant="body2"
                          noWrap={!isMobile}
                          sx={{
                            fontWeight: 500,
                            ...(isMobile
                              ? {
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }
                              : {})
                          }}
                        >
                          {site.location}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={4} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DeviceHub sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                      <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Devices
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {waterCount} water • {electricalCount} electrical • {devices.length} total
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={6} sm={4} md={4}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Receipt sx={{ mr: 0.5, color: theme.palette.primary.main, fontSize: iconSize }} />
                      <Box sx={{ overflow: 'hidden', minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          Updated
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                          {dashboardData.todayDate ? formatDate(dashboardData.todayDate) : "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 2, sm: 3 } }}>
          <Card elevation={2}>
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab label="Monitor" />
              <Tab label="Notifications" />
              <Tab label="Network" />
            </Tabs>

            {/* Network Tab */}
            {activeTab === 2 && (
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <ToggleButtonGroup
                    value={usageSubTab}
                    exclusive
                    onChange={handleUsageSubTabChange}
                    aria-label="device type"
                  >
                    <ToggleButton 
                      value="water" 
                      aria-label="water devices" 
                      sx={{ 
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: '#2196f3',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#1976d2',
                          }
                        }
                      }}
                    >
                      <WaterDrop fontSize="small" sx={{ mr: 0.5 }} />
                      Water
                    </ToggleButton>
                    <ToggleButton 
                      value="electrical" 
                      aria-label="electrical devices" 
                      sx={{ 
                        textTransform: 'none',
                        '&.Mui-selected': {
                          backgroundColor: '#ff9800',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: '#f57c00',
                          }
                        }
                      }}
                    >
                      <Bolt fontSize="small" sx={{ mr: 0.5 }} />
                      Electrical
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Bulk Meter Setup
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Choose one or more meters that should be treated as bulk meters for this {usageSubTab === 'electrical' ? 'electrical' : 'water'} network.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Autocomplete
                        multiple
                        sx={{ minWidth: { xs: '100%', sm: 360 }, flex: 1 }}
                        options={networkDevices}
                        getOptionLabel={formatNetworkDeviceLabel}
                        value={selectedNetworkBulkMeters}
                        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                        onChange={(event, newValue) => {
                          const nextIds = Array.from(new Set((newValue || []).map((d) => String(d.id)).filter(Boolean)));
                          setNetworkBulkMeterIds(nextIds);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Bulk Meters"
                            size="small"
                            placeholder="Select one or more meters"
                          />
                        )}
                        disabled={networkBulkMeterSaving || networkDevices.length === 0}
                      />
                      {canConfigureBulkMeter && (
                        <Button
                          variant="contained"
                          onClick={handleApplyBulkMeter}
                          disabled={networkBulkMeterSaving || (networkBulkMeterIds || []).length === 0 || networkDevices.length === 0}
                        >
                          {networkBulkMeterSaving ? 'Applying...' : 'Save Bulk Meters'}
                        </Button>
                      )}
                      {canConfigureBulkMeter && (
                        <Button
                          variant="outlined"
                          onClick={handleSaveNetworkParentAssignments}
                          disabled={networkParentSaving || networkDevices.length === 0 || networkBulkParentOptions.length === 0}
                        >
                          {networkParentSaving ? 'Saving Parents...' : 'Save Parent Assignments'}
                        </Button>
                      )}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      Current detected bulk meter(s): {detectedNetworkBulkMeters.length > 0
                        ? detectedNetworkBulkMeters.map((d) => formatNetworkDeviceLabel(d)).join(', ')
                        : 'Not set (no parent node found yet)'}
                    </Typography>
                  </CardContent>
                </Card>

                <DeviceFlowChart
                  deviceHierarchy={networkHierarchy}
                  parentOptions={networkBulkParentOptions}
                  parentOptionIds={networkBulkParentOptionIds}
                  parentAssignments={networkParentAssignments}
                  onParentAssignmentChange={handleNetworkParentAssignmentChange}
                  canEditParents={canConfigureBulkMeter}
                  parentAssignmentSaving={networkParentSaving}
                  formatParentOptionLabel={formatNetworkParentOptionLabel}
                  signalThresholdReadings={site?.signalStrengthThresholdReadings}
                />
              </CardContent>
            )}

            {/* Monitor Tab */}
            {activeTab === 0 && (
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" />
                      Monitor Usage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Daily usage totals include device multipliers.
                      {monitorDataUpdatedAt ? ` Last refreshed: ${format(monitorDataUpdatedAt, 'yyyy-MM-dd HH:mm:ss')}` : ''}
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={usageSubTab}
                    exclusive
                    onChange={handleUsageSubTabChange}
                    aria-label="device type"
                    size="small"
                  >
                    <ToggleButton value="water" aria-label="water devices" sx={{ textTransform: 'none' }}>
                      <WaterDrop fontSize="small" sx={{ mr: 0.5 }} />
                      Water
                    </ToggleButton>
                    <ToggleButton value="electrical" aria-label="electrical devices" sx={{ textTransform: 'none' }}>
                      <Bolt fontSize="small" sx={{ mr: 0.5 }} />
                      Electrical
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                {monitorError && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography color="error.dark">{monitorError}</Typography>
                  </Box>
                )}

                {monitorLastDayEnabled && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title="Last Day"
                    subheader={monitorLatestDate
                      ? `Date: ${formatDate(monitorLatestDate)}${monitorLastActiveIndex >= 0 && monitorLastActiveIndex < ((monitorDates || []).length - 1) ? ' (last active day)' : ''}`
                      : 'No daily data yet'}
                    titleTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
                    subheaderTypographyProps={{ variant: 'caption' }}
                    sx={{ py: 1 }}
                    action={
                      <IconButton size="small" onClick={() => setMonitorShowLastDay((prev) => !prev)}>
                        {monitorShowLastDay ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    }
                  />
                  {monitorShowLastDay && (
                    <>
                      <Divider />
                      <CardContent sx={{ p: 1.5 }}>
                        {monitorLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <Grid container spacing={1}>
                            <Grid item xs={6} sm={4} md={2}>
                              <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                                <Typography variant="caption" color="text.secondary">Site Total (1d)</Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {Number(monitorLastDayTotal || 0).toFixed(0)} {usageSubTab === 'electrical' ? 'kWh' : 'L'}
                                </Typography>
                              </Card>
                            </Grid>
                            <Grid item xs={6} sm={4} md={2}>
                              <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                                <Typography variant="caption" color="text.secondary">Bulk Total (1d)</Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {monitorLastDayBulkTotal == null
                                    ? '-'
                                    : `${Number(monitorLastDayBulkTotal).toFixed(0)} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`}
                                </Typography>
                              </Card>
                            </Grid>
                            <Grid item xs={12} sm={4} md={2}>
                              <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                                <Typography variant="caption" color="text.secondary">Top Device (1d)</Typography>
                                <Typography variant="body2" noWrap sx={{ lineHeight: 1.2, maxWidth: '100%' }}>
                                  {monitorLastDayTopDeviceLabel || '-'}
                                </Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {monitorLastDayTopDeviceTotal == null
                                    ? '-'
                                    : `${Number(monitorLastDayTopDeviceTotal).toFixed(0)} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`}
                                </Typography>
                              </Card>
                            </Grid>
                            <Grid item xs={4} sm={4} md={2}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setSiteNotificationsType('leak_detected');
                                  setSiteNotificationsStatus('');
                                  setActiveTab(1);
                                }}
                                sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                              >
                                <Typography variant="caption" color="text.secondary">Leaks (1d)</Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {monitorNotifSummaryLoading ? '-' : String(monitorLastDayLeaks)}
                                </Typography>
                              </Card>
                            </Grid>
                            <Grid item xs={4} sm={4} md={2}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setSiteNotificationsType('signal_strength');
                                  setSiteNotificationsStatus('');
                                  setActiveTab(1);
                                }}
                                sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                              >
                                <Typography variant="caption" color="text.secondary">Low Signal (1d)</Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {monitorNotifSummaryLoading ? '-' : String(monitorLastDayLowSignal)}
                                </Typography>
                              </Card>
                            </Grid>
                            <Grid item xs={4} sm={4} md={2}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setSiteNotificationsType('threshold_exceeded');
                                  setSiteNotificationsStatus('');
                                  setActiveTab(1);
                                }}
                                sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                              >
                                <Typography variant="caption" color="text.secondary">Thresholds (1d)</Typography>
                                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                  {monitorNotifSummaryLoading ? '-' : String(monitorLastDayThresholds)}
                                </Typography>
                              </Card>
                            </Grid>
                          </Grid>
                        )}

                        {(monitorLoading || monitorNotifSummaryLoading) ? null : (
                          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                            <Grid item xs={12} md={6}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setSiteNotificationsType('leak_detected');
                                  setSiteNotificationsStatus('');
                                  setActiveTab(1);
                                }}
                                sx={{ p: 1, height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Top 10 Leaks (1d)
                                  </Typography>
                                  <Typography variant="caption" color="primary">View all</Typography>
                                </Box>
                                {monitorNotifSummaryLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                    <CircularProgress size={18} />
                                  </Box>
                                ) : monitorLastDayTopLeaks.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No leaks detected.</Typography>
                                ) : (
                                  <List dense disablePadding>
                                    {monitorLastDayTopLeaks.map((leak, idx) => (
                                      <ListItem key={leak.id} disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                          primary={`${idx + 1}. ${leak.serialNumber}`}
                                          secondary={leak.description}
                                        />
                                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                          <Tooltip title="Possible Leak per hour from the minimum hourly value that still meets the threshold in the configured period (24h by default)">
                                            <Chip
                                              size="small"
                                              color="error"
                                              label={leak.estLitresPerHour != null ? `Possible Leak ~${leak.estLitresPerHour.toLocaleString('en-US')} L/hour` : 'Possible Leak n/a'}
                                            />
                                          </Tooltip>
                                          <Chip size="small" variant="outlined" label={`${leak.leakHours}h streak`} />
                                        </Box>
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setConnectedDeviceSortByUsage(true);
                                  setTimeout(() => {
                                    connectedDevicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 50);
                                }}
                                sx={{ p: 1, height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Top 10 Consumers (1d)
                                  </Typography>
                                  <Typography variant="caption" color="primary">View all</Typography>
                                </Box>
                                {monitorLastDayTopConsumers.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No consumption data.</Typography>
                                ) : (
                                  <List dense disablePadding>
                                    {monitorLastDayTopConsumers.map((c, idx) => (
                                      <ListItem key={c.id} disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                          primary={`${idx + 1}. ${c.serialNumber}`}
                                          secondary={c.description}
                                        />
                                        <Chip
                                          size="small"
                                          label={`${Number(c.total).toLocaleString('en-US', { maximumFractionDigits: 0 })} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`}
                                          sx={{ ml: 1, flexShrink: 0 }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Card>
                            </Grid>
                          </Grid>
                        )}

                        {(monitorLoading || monitorNotifSummaryLoading) ? null : (
                          <Box sx={{ mt: 1.5 }}>
                            {monitorLatestDate ? (
                              monitorLastDayBulkTotal == null ? (
                                <Box sx={{ height: 220 }}>
                                  <Chart
                                    type="bar"
                                    data={{
                                      labels: [formatDate(monitorLatestDate)],
                                      datasets: [
                                        {
                                          label: `Total (${usageSubTab === 'electrical' ? 'kWh' : 'L'})`,
                                          data: [Number(monitorLastDayTotal || 0)],
                                          type: 'bar',
                                          borderColor: usageSubTab === 'electrical' ? '#ff9800' : '#2196f3',
                                          backgroundColor: usageSubTab === 'electrical' ? 'rgba(255, 152, 0, 0.35)' : 'rgba(33, 150, 243, 0.35)'
                                        }
                                      ]
                                    }}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      color: chartTextColor,
                                      plugins: {
                                        legend: {
                                          position: 'top',
                                          labels: {
                                            color: chartTextColor,
                                            font: { weight: '500' }
                                          }
                                        },
                                        barValueLabels: { display: false },
                                        barValueLabelsV2: { display: false },
                                        barValueLabelsV3: false,
                                        barValueLabelsV4: {
                                          display: true,
                                          color: chartTextColor,
                                          fontSize: isMobile ? 9 : 10,
                                          offset: 6,
                                          minValue: 0,
                                          position: 'middle',
                                          includeLineDatasets: false
                                        },
                                        title: { display: false },
                                        tooltip: {
                                          backgroundColor: chartTooltipBgColor,
                                          titleColor: chartTooltipTextColor,
                                          bodyColor: chartTooltipTextColor,
                                          footerColor: chartTooltipTextColor,
                                          borderColor: chartTooltipBorderColor,
                                          borderWidth: 1,
                                          cornerRadius: 6,
                                          callbacks: {
                                            label: (ctx) => `${ctx.dataset.label}: ${ctx.formattedValue}`
                                          }
                                        }
                                      },
                                      scales: {
                                        x: {
                                          ticks: { color: chartTextColor, font: { weight: '500' } },
                                          grid: { color: chartGridColor },
                                          border: { color: chartGridColor }
                                        },
                                        y: {
                                          beginAtZero: true,
                                          ticks: { color: chartTextColor, font: { weight: '500' } },
                                          grid: { color: chartGridColor },
                                          border: { color: chartGridColor }
                                        }
                                      }
                                    }}
                                  />
                                </Box>
                              ) : (
                                <Box sx={{ height: 220 }}>
                                  <Chart
                                    type="bar"
                                    data={(() => {
                                      const bulk = Number(monitorLastDayBulkTotal || 0);
                                      const site = Number(monitorLastDayTotal || 0);
                                      const delta = bulk - site > 0 ? bulk - site : 0;

                                      return {
                                        labels: [formatDate(monitorLatestDate)],
                                        datasets: [
                                          {
                                            label: `Base (${usageSubTab === 'electrical' ? 'kWh' : 'L'})`,
                                            data: [site],
                                            backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                            borderColor: 'rgba(33, 150, 243, 0.4)',
                                            borderWidth: 1,
                                            stack: 'balance'
                                          },
                                          {
                                            label: 'Delta',
                                            data: [delta],
                                            backgroundColor: [delta > 0 ? 'rgba(255, 152, 0, 0.75)' : 'rgba(0, 0, 0, 0)'],
                                            borderColor: [delta > 0 ? 'rgba(255, 152, 0, 1)' : 'rgba(0, 0, 0, 0)'],
                                            borderWidth: 1,
                                            stack: 'balance'
                                          }
                                        ]
                                      };
                                    })()}
                                    options={{
                                      responsive: true,
                                      maintainAspectRatio: false,
                                      color: chartTextColor,
                                      plugins: {
                                        legend: {
                                          position: 'top',
                                          labels: {
                                            color: chartTextColor,
                                            font: { weight: '500' }
                                          }
                                        },
                                        barValueLabels: { display: false },
                                        barValueLabelsV2: { display: false },
                                        barValueLabelsV3: false,
                                        barValueLabelsV4: {
                                          display: true,
                                          color: chartTextColor,
                                          insideColor: chartTextColor,
                                          fontSize: isMobile ? 9 : 10,
                                          offset: 6,
                                          minValue: 0,
                                          position: 'middle',
                                          positionByDatasetIndex: { 0: 'middle', 1: 'insideTop' },
                                          colorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                          insideColorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                          includeLineDatasets: false,
                                          formatter: (v, ctx) => {
                                            if (ctx?.datasetIndex !== 0 && ctx?.datasetIndex !== 1) return '';
                                            if (ctx?.datasetIndex === 0) {
                                              const total = Number(monitorLastDayTotal || 0);
                                              if (!Number.isFinite(total) || total === 0) return '';
                                              return Math.round(total).toLocaleString('en-US');
                                            }

                                            const num = Number(v);
                                            if (!Number.isFinite(num) || num === 0) return '';
                                            return Math.round(num).toLocaleString('en-US');
                                          }
                                        },
                                        title: { display: false },
                                        tooltip: {
                                          backgroundColor: chartTooltipBgColor,
                                          titleColor: chartTooltipTextColor,
                                          bodyColor: chartTooltipTextColor,
                                          footerColor: chartTooltipTextColor,
                                          borderColor: chartTooltipBorderColor,
                                          borderWidth: 1,
                                          cornerRadius: 6,
                                          callbacks: {
                                            label: (ctx) => {
                                              if (ctx?.datasetIndex !== 1) return '';
                                              const bulk = Number(monitorLastDayBulkTotal || 0);
                                              const site = Number(monitorLastDayTotal || 0);
                                              const delta = bulk - site;
                                              const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                              if (!Number.isFinite(delta) || delta <= 0) return '';
                                              return `Delta: ${Math.round(delta).toLocaleString('en-US')} ${unit}`;
                                            },
                                            afterBody: () => {
                                              const bulk = Number(monitorLastDayBulkTotal || 0);
                                              const site = Number(monitorLastDayTotal || 0);
                                              const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                              return [
                                                `Bulk: ${Math.round(bulk).toLocaleString('en-US')} ${unit}`,
                                                `Site: ${Math.round(site).toLocaleString('en-US')} ${unit}`
                                              ];
                                            }
                                          }
                                        }
                                      },
                                      scales: {
                                        x: {
                                          stacked: true,
                                          ticks: { color: chartTextColor, font: { weight: '500' } },
                                          grid: { color: chartGridColor },
                                          border: { color: chartGridColor }
                                        },
                                        y: {
                                          stacked: true,
                                          beginAtZero: true,
                                          ticks: { color: chartTextColor, font: { weight: '500' } },
                                          grid: { color: chartGridColor },
                                          border: { color: chartGridColor }
                                        }
                                      }
                                    }}
                                  />
                                </Box>
                              )
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No usage data available for this day.
                              </Typography>
                            )}
                          </Box>
                        )}

                      </CardContent>
                    </>
                  )}
                </Card>
                )}

                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardHeader
                    title="Last 7 Days"
                    titleTypographyProps={{ variant: 'subtitle2', fontWeight: 600 }}
                    sx={{ py: 1 }}
                    action={
                      <IconButton size="small" onClick={() => setMonitorShowLast14Days((prev) => !prev)}>
                        {monitorShowLast14Days ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                      </IconButton>
                    }
                  />
                  {monitorShowLast14Days && (
                    <>
                      <Divider />
                      <CardContent sx={{ p: 1.5 }}>
                        {monitorLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <>
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={2} md={2}>
                            <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                              <Typography variant="caption" color="text.secondary">Site Total (7d)</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {(monitorTotalSeries || []).reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(0)}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={6} sm={2} md={2}>
                            <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                              <Typography variant="caption" color="text.secondary">Bulk Total (7d)</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {monitorBulkSeries
                                  ? (monitorBulkSeries || []).reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(0)
                                  : '-'}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} sm={2} md={2}>
                            <Card variant="outlined" sx={{ p: 1, minHeight: 88, overflow: 'hidden' }}>
                              <Typography variant="caption" color="text.secondary">Top Device (7d)</Typography>
                              <Typography variant="body2" noWrap sx={{ lineHeight: 1.2, maxWidth: '100%' }}>
                                {monitorTopDeviceLabel || '-'}
                              </Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {monitorTopDeviceTotal == null
                                  ? '-'
                                  : `${Number(monitorTopDeviceTotal).toFixed(0)} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={4} sm={2} md={2}>
                            <Card
                              variant="outlined"
                              onClick={() => {
                                setSiteNotificationsType('leak_detected');
                                setSiteNotificationsStatus('');
                                setActiveTab(1);
                              }}
                              sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                            >
                              <Typography variant="caption" color="text.secondary">Leaks (7d)</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {monitorNotifSummaryLoading ? '-' : String(monitorActiveLeaks)}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={4} sm={2} md={2}>
                            <Card
                              variant="outlined"
                              onClick={() => {
                                setSiteNotificationsType('signal_strength');
                                setSiteNotificationsStatus('');
                                setActiveTab(1);
                              }}
                              sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                            >
                              <Typography variant="caption" color="text.secondary">Low Signal (7d)</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {monitorNotifSummaryLoading ? '-' : String(monitorActiveLowSignal)}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={4} sm={2} md={2}>
                            <Card
                              variant="outlined"
                              onClick={() => {
                                setSiteNotificationsType('threshold_exceeded');
                                setSiteNotificationsStatus('');
                                setActiveTab(1);
                              }}
                              sx={{ p: 1, minHeight: 88, overflow: 'hidden', cursor: 'pointer' }}
                            >
                              <Typography variant="caption" color="text.secondary">Thresholds (7d)</Typography>
                              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                                {monitorNotifSummaryLoading ? '-' : String(monitorActiveThresholds)}
                              </Typography>
                            </Card>
                          </Grid>
                        </Grid>

                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={6}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setSiteNotificationsType('leak_detected');
                                  setSiteNotificationsStatus('');
                                  setActiveTab(1);
                                }}
                                sx={{ p: 1, height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Top 10 Leaks (7d)
                                  </Typography>
                                  <Typography variant="caption" color="primary">View all</Typography>
                                </Box>
                                {monitorNotifSummaryLoading ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                    <CircularProgress size={18} />
                                  </Box>
                                ) : monitor14DayTopLeaks.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No leaks detected.</Typography>
                                ) : (
                                  <List dense disablePadding>
                                    {monitor14DayTopLeaks.map((leak, idx) => (
                                      <ListItem key={leak.id} disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                          primary={`${idx + 1}. ${leak.serialNumber}`}
                                          secondary={leak.description}
                                        />
                                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
                                          <Tooltip title="Possible Leak per hour from the minimum hourly value that still meets the threshold in the configured period (24h by default)">
                                            <Chip
                                              size="small"
                                              color="error"
                                              label={leak.estLitresPerHour != null ? `Possible Leak ~${leak.estLitresPerHour.toLocaleString('en-US')} L/hour` : 'Possible Leak n/a'}
                                            />
                                          </Tooltip>
                                          <Chip size="small" variant="outlined" label={`${leak.leakHours}h streak`} />
                                        </Box>
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Card>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Card
                                variant="outlined"
                                onClick={() => {
                                  setConnectedDeviceSortByUsage(true);
                                  setTimeout(() => {
                                    connectedDevicesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }, 50);
                                }}
                                sx={{ p: 1, height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Top 10 Consumers (7d)
                                  </Typography>
                                  <Typography variant="caption" color="primary">View all</Typography>
                                </Box>
                                {monitorTopConsumers.length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">No consumption data.</Typography>
                                ) : (
                                  <List dense disablePadding>
                                    {monitorTopConsumers.map((c, idx) => (
                                      <ListItem key={c.id} disableGutters sx={{ py: 0.25 }}>
                                        <ListItemText
                                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                                          primary={`${idx + 1}. ${c.serialNumber}`}
                                          secondary={c.description}
                                        />
                                        <Chip
                                          size="small"
                                          label={`${Number(c.total).toLocaleString('en-US', { maximumFractionDigits: 0 })} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`}
                                          sx={{ ml: 1, flexShrink: 0 }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </Card>
                            </Grid>
                          </Grid>

                        {monitorBulkSeries ? null : (
                          <Box sx={{ height: 300 }}>
                            <Chart
                              type="bar"
                              data={{
                                labels: monitorDates.map((d) => formatDate(d)),
                                datasets: [
                                  {
                                    label: `Total (${usageSubTab === 'electrical' ? 'kWh' : 'L'})`,
                                    data: monitorTotalSeries,
                                    type: 'bar',
                                    borderColor: usageSubTab === 'electrical' ? '#ff9800' : '#2196f3',
                                    backgroundColor: usageSubTab === 'electrical' ? 'rgba(255, 152, 0, 0.35)' : 'rgba(33, 150, 243, 0.35)'
                                  }
                                ]
                              }}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                color: chartTextColor,
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    labels: {
                                      color: chartTextColor,
                                      font: { weight: '500' }
                                    }
                                  },
                                  barValueLabels: { display: false },
                                  barValueLabelsV2: { display: false },
                                  barValueLabelsV3: false,
                                  barValueLabelsV4: {
                                    display: true,
                                    color: chartTextColor,
                                    fontSize: isMobile ? 9 : 10,
                                    offset: 6,
                                    minValue: 0,
                                    position: 'middle',
                                    includeLineDatasets: false
                                  },
                                  title: { display: false },
                                  tooltip: {
                                    backgroundColor: chartTooltipBgColor,
                                    titleColor: chartTooltipTextColor,
                                    bodyColor: chartTooltipTextColor,
                                    footerColor: chartTooltipTextColor,
                                    borderColor: chartTooltipBorderColor,
                                    borderWidth: 1,
                                    cornerRadius: 6,
                                    callbacks: {
                                      label: (ctx) => {
                                        return `${ctx.dataset.label}: ${ctx.formattedValue}`;
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  x: {
                                    ticks: { color: chartTextColor, font: { weight: '500' } },
                                    grid: { color: chartGridColor },
                                    border: { color: chartGridColor }
                                  },
                                  y: {
                                    beginAtZero: true,
                                    ticks: { color: chartTextColor, font: { weight: '500' } },
                                    grid: { color: chartGridColor },
                                    border: { color: chartGridColor }
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}

                        {!monitorLoading && monitorBulkSeries && Array.isArray(monitorBulkSeries) && Array.isArray(monitorDates) && Array.isArray(monitorTotalSeries) && (
                          <Box sx={{ height: 260, mt: 0 }}>
                            <Chart
                              type="bar"
                              data={(() => {
                                const bulk = (monitorBulkSeries || []).map((v) => Number(v) || 0);
                                const site = (monitorTotalSeries || []).map((v) => Number(v) || 0);
                                const base = site;
                                const delta = site.map((s, i) => {
                                  const b = Number(bulk[i] || 0);
                                  const d = b - s;
                                  return d > 0 ? d : 0;
                                });
                                const deltaBg = delta.map((d) => (Number(d) > 0 ? 'rgba(255, 152, 0, 0.75)' : 'rgba(0, 0, 0, 0)'));

                                return {
                                  labels: (monitorDates || []).map((d) => formatDate(d)),
                                  datasets: [
                                    {
                                      label: `Base (${usageSubTab === 'electrical' ? 'kWh' : 'L'})`,
                                      data: base,
                                      backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                      borderColor: 'rgba(33, 150, 243, 0.4)',
                                      borderWidth: 1,
                                      stack: 'balance'
                                    },
                                    {
                                      label: 'Delta',
                                      data: delta,
                                      backgroundColor: deltaBg,
                                      borderColor: deltaBg.map((c) => (c === 'rgba(0, 0, 0, 0)' ? 'rgba(0, 0, 0, 0)' : c.replace('0.75', '1'))),
                                      borderWidth: 1,
                                      stack: 'balance'
                                    }
                                  ]
                                };
                              })()}
                              options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                color: chartTextColor,
                                plugins: {
                                  legend: {
                                    position: 'top',
                                    labels: {
                                      color: chartTextColor,
                                      font: { weight: '500' }
                                    }
                                  },
                                  barValueLabels: { display: false },
                                  barValueLabelsV2: { display: false },
                                  barValueLabelsV3: false,
                                  barValueLabelsV4: {
                                    display: true,
                                    color: chartTextColor,
                                    insideColor: chartTextColor,
                                    fontSize: isMobile ? 9 : 10,
                                    offset: 6,
                                    minValue: 0,
                                    position: 'middle',
                                    positionByDatasetIndex: { 0: 'middle', 1: 'insideTop' },
                                    colorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                    insideColorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                    includeLineDatasets: false,
                                    formatter: (v, ctx) => {
                                      if (ctx?.datasetIndex !== 0 && ctx?.datasetIndex !== 1) return '';
                                      const idx = ctx?.index;

                                      if (ctx?.datasetIndex === 0) {
                                        const total = Number(monitorTotalSeries?.[idx] || 0);
                                        if (!Number.isFinite(total) || total === 0) return '';
                                        return Math.round(total).toLocaleString('en-US');
                                      }

                                      const num = Number(v);
                                      if (!Number.isFinite(num) || num === 0) return '';
                                      return Math.round(num).toLocaleString('en-US');
                                    }
                                  },
                                  title: { display: false },
                                  tooltip: {
                                    backgroundColor: chartTooltipBgColor,
                                    titleColor: chartTooltipTextColor,
                                    bodyColor: chartTooltipTextColor,
                                    footerColor: chartTooltipTextColor,
                                    borderColor: chartTooltipBorderColor,
                                    borderWidth: 1,
                                    cornerRadius: 6,
                                    callbacks: {
                                      label: (ctx) => {
                                        if (ctx?.datasetIndex !== 1) return '';
                                        const idx = ctx?.dataIndex;
                                        const bulk = Number(monitorBulkSeries?.[idx] || 0);
                                        const site = Number(monitorTotalSeries?.[idx] || 0);
                                        const delta = bulk - site;
                                        const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                        if (!Number.isFinite(delta) || delta <= 0) return '';
                                        return `Delta: ${Math.round(delta).toLocaleString('en-US')} ${unit}`;
                                      },
                                      afterBody: (tooltipItems) => {
                                        const idx = tooltipItems?.[0]?.dataIndex;
                                        const bulk = Number(monitorBulkSeries?.[idx] || 0);
                                        const site = Number(monitorTotalSeries?.[idx] || 0);
                                        const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                        return [
                                          `Bulk: ${Math.round(bulk).toLocaleString('en-US')} ${unit}`,
                                          `Site: ${Math.round(site).toLocaleString('en-US')} ${unit}`
                                        ];
                                      }
                                    }
                                  }
                                },
                                scales: {
                                  x: {
                                    stacked: true,
                                    ticks: { color: chartTextColor, font: { weight: '500' } },
                                    grid: { color: chartGridColor },
                                    border: { color: chartGridColor }
                                  },
                                  y: {
                                    stacked: true,
                                    beginAtZero: true,
                                    ticks: { color: chartTextColor, font: { weight: '500' } },
                                    grid: { color: chartGridColor },
                                    border: { color: chartGridColor }
                                  }
                                }
                              }}
                            />
                          </Box>
                        )}

                        {!monitorLoading && monitorBulkBreakdownEntries.length > 1 && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Per Bulk Meter (7d)
                            </Typography>
                            <Grid container spacing={1}>
                              {monitorBulkBreakdownEntries.map((entry) => {
                                const bulk = (entry?.bulkSeries || []).map((v) => Number(v) || 0);
                                const base = (entry?.baseSeries || []).map((v) => Number(v) || 0);
                                const delta = base.map((s, i) => {
                                  const b = Number(bulk[i] || 0);
                                  const d = b - s;
                                  return d > 0 ? d : 0;
                                });
                                const deltaBg = delta.map((d) => (Number(d) > 0 ? 'rgba(255, 152, 0, 0.75)' : 'rgba(0, 0, 0, 0)'));

                                return (
                                  <Grid item xs={12} md={6} key={entry.bulkId}>
                                    <Card variant="outlined" sx={{ p: 1 }}>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                        {entry.label}{entry.serialNumber ? ` (${entry.serialNumber})` : ''}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                        7d Bulk: {bulk.reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(0)} {usageSubTab === 'electrical' ? 'kWh' : 'L'} | Base: {base.reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(0)} {usageSubTab === 'electrical' ? 'kWh' : 'L'}
                                      </Typography>
                                      <Box sx={{ height: 210 }}>
                                        <Chart
                                          type="bar"
                                          data={{
                                            labels: (monitorDates || []).map((d) => formatDate(d)),
                                            datasets: [
                                              {
                                                label: `Base (${usageSubTab === 'electrical' ? 'kWh' : 'L'})`,
                                                data: base,
                                                backgroundColor: 'rgba(33, 150, 243, 0.25)',
                                                borderColor: 'rgba(33, 150, 243, 0.4)',
                                                borderWidth: 1,
                                                stack: 'balance'
                                              },
                                              {
                                                label: 'Delta',
                                                data: delta,
                                                backgroundColor: deltaBg,
                                                borderColor: deltaBg.map((c) => (c === 'rgba(0, 0, 0, 0)' ? 'rgba(0, 0, 0, 0)' : c.replace('0.75', '1'))),
                                                borderWidth: 1,
                                                stack: 'balance'
                                              }
                                            ]
                                          }}
                                          options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            color: chartTextColor,
                                            plugins: {
                                              legend: {
                                                position: 'top',
                                                labels: {
                                                  color: chartTextColor,
                                                  font: { weight: '500' }
                                                }
                                              },
                                              barValueLabels: { display: false },
                                              barValueLabelsV2: { display: false },
                                              barValueLabelsV3: false,
                                              barValueLabelsV4: {
                                                display: true,
                                                color: chartTextColor,
                                                insideColor: chartTextColor,
                                                fontSize: isMobile ? 9 : 10,
                                                offset: 6,
                                                minValue: 0,
                                                position: 'middle',
                                                positionByDatasetIndex: { 0: 'middle', 1: 'insideTop' },
                                                colorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                                insideColorByDatasetIndex: { 0: chartTextColor, 1: chartTextColor },
                                                includeLineDatasets: false,
                                                formatter: (v, ctx) => {
                                                  if (ctx?.datasetIndex !== 0 && ctx?.datasetIndex !== 1) return '';
                                                  const idx = ctx?.index;
                                                  if (ctx?.datasetIndex === 0) {
                                                    const total = Number(base?.[idx] || 0);
                                                    if (!Number.isFinite(total) || total === 0) return '';
                                                    return Math.round(total).toLocaleString('en-US');
                                                  }
                                                  const num = Number(v);
                                                  if (!Number.isFinite(num) || num === 0) return '';
                                                  return Math.round(num).toLocaleString('en-US');
                                                }
                                              },
                                              title: { display: false },
                                              tooltip: {
                                                backgroundColor: chartTooltipBgColor,
                                                titleColor: chartTooltipTextColor,
                                                bodyColor: chartTooltipTextColor,
                                                footerColor: chartTooltipTextColor,
                                                borderColor: chartTooltipBorderColor,
                                                borderWidth: 1,
                                                cornerRadius: 6,
                                                callbacks: {
                                                  label: (ctx) => {
                                                    if (ctx?.datasetIndex !== 1) return '';
                                                    const idx = ctx?.dataIndex;
                                                    const b = Number(bulk?.[idx] || 0);
                                                    const s = Number(base?.[idx] || 0);
                                                    const d = b - s;
                                                    const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                                    if (!Number.isFinite(d) || d <= 0) return '';
                                                    return `Delta: ${Math.round(d).toLocaleString('en-US')} ${unit}`;
                                                  },
                                                  afterBody: (tooltipItems) => {
                                                    const idx = tooltipItems?.[0]?.dataIndex;
                                                    const b = Number(bulk?.[idx] || 0);
                                                    const s = Number(base?.[idx] || 0);
                                                    const unit = usageSubTab === 'electrical' ? 'kWh' : 'L';
                                                    return [
                                                      `Bulk: ${Math.round(b).toLocaleString('en-US')} ${unit}`,
                                                      `Base: ${Math.round(s).toLocaleString('en-US')} ${unit}`
                                                    ];
                                                  }
                                                }
                                              }
                                            },
                                            scales: {
                                              x: {
                                                stacked: true,
                                                ticks: { color: chartTextColor, font: { weight: '500' } },
                                                grid: { color: chartGridColor },
                                                border: { color: chartGridColor }
                                              },
                                              y: {
                                                stacked: true,
                                                beginAtZero: true,
                                                ticks: { color: chartTextColor, font: { weight: '500' } },
                                                grid: { color: chartGridColor },
                                                border: { color: chartGridColor }
                                              }
                                            }
                                          }}
                                        />
                                      </Box>
                                    </Card>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Box>
                        )}

                          </>
                        )}
                      </CardContent>
                    </>
                  )}
                </Card>

                <Box sx={{ mt: 2 }} ref={connectedDevicesRef}>
                  <Card variant="outlined">
                    <CardHeader
                      title="Connected Devices"
                      titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                      sx={{ py: 1 }}
                      action={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => setConnectedDeviceSortByUsage((prev) => !prev)}
                          >
                            {connectedDeviceSortByUsage ? 'Sort A\u2013Z' : 'Sort by usage'}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleExportSiteDevicesCSV}
                            disabled={siteCsvExporting}
                          >
                            Export
                          </Button>
                        </Box>
                      }
                    />
                    <Divider />
                    <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search devices..."
                        value={connectedDeviceSearch}
                        onChange={(e) => setConnectedDeviceSearch(e.target.value || '')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: connectedDeviceSearch ? (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => setConnectedDeviceSearch('')}
                              >
                                <Clear fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                        }}
                        sx={{ mb: 1.5 }}
                      />

                      {(() => {
                        const isElectrical = usageSubTab === 'electrical';
                        const predicate = (d) => isElectrical ? d?.deviceType === 'electrical_meter' : d?.deviceType !== 'electrical_meter';
                        const list = (devices || []).filter(predicate);

                        const q = (connectedDeviceSearch || '').trim().toLowerCase();
                        const filtered = q
                          ? list.filter((d) => {
                              const hay = `${d?.serialNumber || ''} ${d?.description || ''} ${d?.location || ''}`.toLowerCase();
                              return hay.includes(q);
                            })
                          : list;

                        const sorted = [...filtered].sort((a, b) => {
                          if (connectedDeviceSortByUsage) {
                            const au = Number(monitorLastDayUsageByDeviceId?.[String(a?.id)] || 0);
                            const bu = Number(monitorLastDayUsageByDeviceId?.[String(b?.id)] || 0);
                            if (bu !== au) return bu - au;
                          }
                          const al = (a?.description || a?.serialNumber || '').toString().toLowerCase();
                          const bl = (b?.description || b?.serialNumber || '').toString().toLowerCase();
                          return al.localeCompare(bl);
                        });

                        if (sorted.length === 0) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              No devices found.
                            </Typography>
                          );
                        }

                        return (
                          <List dense disablePadding>
                            {sorted.map((d, idx) => (
                              <React.Fragment key={d.id}>
                                <ListItem disablePadding>
                                  <ListItemButton
                                    sx={{ py: 0.5 }}
                                    onClick={() =>
                                      navigate(`/devices/${d.id}`, {
                                        state: {
                                          returnTo: `${location.pathname}${location.search || ''}`,
                                          returnState: {
                                            activeTab,
                                            usageSubTab,
                                            connectedDeviceSearch,
                                            siteNotificationsType,
                                            siteNotificationsStatus
                                          },
                                          returnScrollY: window.scrollY
                                        }
                                      })
                                    }
                                  >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      {d?.deviceType === 'electrical_meter' ? (
                                        <Bolt fontSize="small" />
                                      ) : (
                                        <WaterDrop fontSize="small" />
                                      )}
                                    </ListItemIcon>
                                    {(() => {
                                      const pending = Number(pendingNotificationCountByDeviceId?.[String(d.id)] || 0);
                                      const pendingTypeCounts = pendingNotificationTypeCountByDeviceId?.[String(d.id)] || {};
                                      const usageValue = Number(monitorLastDayUsageByDeviceId?.[String(d.id)] || 0);
                                      const label = d?.description || d?.serialNumber || 'Device';

                                      const sn = d?.serialNumber || '';
                                      const loc = d?.location || '';
                                      const lastReadingAt = d?.lastReading || d?.lastReadingTime || null;
                                      const lastReadingVal = d?.lastReadingValue ?? null;
                                      const currentMultiplier = Number(d?.multiplier);
                                      const multiplierLabel = Number.isFinite(currentMultiplier) && currentMultiplier > 0
                                        ? `x${currentMultiplier}`
                                        : 'x1';

                                      let lastAtText = '-';
                                      if (lastReadingAt) {
                                        try {
                                          lastAtText = format(new Date(lastReadingAt), 'dd/MM/yyyy HH:mm');
                                        } catch {
                                          lastAtText = String(lastReadingAt);
                                        }
                                      }

                                      let lastValText = '-';
                                      if (lastReadingVal !== null && lastReadingVal !== undefined && lastReadingVal !== '') {
                                        const n = Number(lastReadingVal);
                                        lastValText = Number.isFinite(n)
                                          ? n.toLocaleString('en-US', { maximumFractionDigits: 3 })
                                          : String(lastReadingVal);
                                      }

                                      const alertSummary = pending > 0
                                        ? Object.entries(pendingTypeCounts)
                                            .sort((a, b) => Number(b?.[1] || 0) - Number(a?.[1] || 0))
                                            .slice(0, 3)
                                            .map(([type, count]) => `${formatAlertTypeLabel(type)} ${count}`)
                                            .join(', ')
                                        : '';

                                      const metadataColumns = [
                                        { label: 'S/N', value: sn || '-' },
                                        { label: 'Location', value: loc || '-' },
                                        { label: 'Multiplier', value: multiplierLabel },
                                        { label: 'Last reading', value: `${lastValText} @ ${lastAtText}` },
                                        { label: 'Open alerts', value: alertSummary || 'None' }
                                      ];

                                      const usageLabel = `${usageSubTab === 'electrical' ? 'Last 1d energy usage' : 'Last 1d water usage'}: ${usageValue.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${usageSubTab === 'electrical' ? 'kWh' : 'L'}`;

                                      return (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            alignItems: { xs: 'stretch', sm: 'flex-start' },
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            width: '100%',
                                            minWidth: 0
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              flex: 1,
                                              minWidth: 0,
                                              overflow: 'hidden',
                                              pr: { sm: 1 }
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontWeight: 600,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              {label}
                                            </Typography>

                                            <Box
                                              sx={{
                                                mt: 0.25,
                                                display: 'grid',
                                                gridTemplateColumns: {
                                                  xs: 'repeat(2, minmax(110px, 1fr))',
                                                  sm: 'repeat(3, minmax(120px, 1fr))',
                                                  md: 'minmax(110px, 1fr) minmax(170px, 1.6fr) minmax(95px, 0.8fr) minmax(170px, 1.6fr)',
                                                  lg: 'minmax(110px, 1fr) minmax(200px, 1.8fr) minmax(100px, 0.8fr) minmax(190px, 1.7fr) minmax(140px, 1.1fr)'
                                                },
                                                columnGap: 1,
                                                rowGap: 0.75
                                              }}
                                            >
                                              {metadataColumns.map((meta) => (
                                                <Box key={meta.label} sx={{ minWidth: 0 }}>
                                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                                                    {meta.label}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      display: 'block',
                                                      fontWeight: 500,
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                      lineHeight: 1.2
                                                    }}
                                                  >
                                                    {meta.value}
                                                  </Typography>
                                                </Box>
                                              ))}
                                            </Box>
                                          </Box>

                                          <Box
                                            sx={{
                                              display: 'flex',
                                              gap: 0.5,
                                              flexShrink: 0,
                                              flexWrap: 'wrap',
                                              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                                              alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                              minWidth: { sm: 230 }
                                            }}
                                          >
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={usageLabel}
                                              sx={{ minWidth: { sm: 160 }, justifyContent: 'center' }}
                                            />
                                            {pending > 0 ? (
                                              <Chip
                                                size="small"
                                                label={`Alerts: ${pending}`}
                                                color="error"
                                                sx={{ minWidth: 74, justifyContent: 'center' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const params = new URLSearchParams();
                                                  params.set('deviceId', String(d.id));
                                                  params.set('deviceLabel', d?.description || d?.serialNumber || `Device ${d.id}`);
                                                  navigate(`/notifications?${params.toString()}`);
                                                }}
                                              />
                                            ) : (
                                              <Chip
                                                size="small"
                                                label="Alerts: 0"
                                                color="success"
                                                variant="outlined"
                                                sx={{ minWidth: 74, justifyContent: 'center' }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                      );
                                    })()}
                                  </ListItemButton>
                                </ListItem>
                                {idx < sorted.length - 1 ? <Divider component="li" sx={{ opacity: 0.6 }} /> : null}
                              </React.Fragment>
                            ))}
                          </List>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </Box>
              </CardContent>
            )}

            {/* Notifications Tab */}
            {activeTab === 1 && (
              <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <TextField
                    select
                    size="small"
                    label="Type"
                    value={siteNotificationsType}
                    onChange={(e) => setSiteNotificationsType(e.target.value)}
                    sx={{ minWidth: 200 }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All</option>
                    <option value="threshold_exceeded">Threshold</option>
                    <option value="leak_detected">Leak</option>
                    <option value="signal_strength">Signal</option>
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Status"
                    value={siteNotificationsStatus}
                    onChange={(e) => setSiteNotificationsStatus(e.target.value)}
                    sx={{ minWidth: 160 }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                  >
                    <option value="">All</option>
                    <option value="new">New</option>
                    <option value="viewed">Viewed</option>
                    <option value="resolved">Resolved</option>
                  </TextField>

                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => loadSiteNotifications()}
                  >
                    Apply
                  </Button>

                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    Showing last 2 days
                  </Typography>
                </Box>

                {siteNotificationsError && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                    <Typography color="error.dark">{siteNotificationsError}</Typography>
                  </Box>
                )}

                {siteNotificationsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <NotificationTable
                    notifications={siteNotifications || []}
                    onUpdateStatus={handleSiteNotificationUpdateStatus}
                    onDelete={handleSiteNotificationDelete}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                    compact
                    showDelete={authUser?.role === 'admin'}
                    deviceLinkState={{
                      returnTo: `${location.pathname}${location.search || ''}`,
                      returnState: {
                        activeTab,
                        usageSubTab,
                        connectedDeviceSearch,
                        siteNotificationsType,
                        siteNotificationsStatus
                      },
                      returnScrollY: window.scrollY
                    }}
                  />
                )}
              </CardContent>
            )}

          </Card>
        </Box>

        <Dialog
          open={siteEditOpen}
          onClose={() => setSiteEditOpen(false)}
          fullWidth
          maxWidth="md"
          scroll="paper"
          PaperProps={{
            sx: {
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Edit Site</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Site Name"
              fullWidth
              value={siteEditForm.description}
              onChange={(e) => setSiteEditForm((p) => ({ ...p, description: e.target.value }))}
            />
            <StructuredLocationFields
              value={siteEditForm.location}
              onChange={(nextLocation) => setSiteEditForm((p) => ({ ...p, location: nextLocation }))}
              title="Location"
            />
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Latitude"
                  fullWidth
                  value={siteEditForm.latitude}
                  onChange={(e) => setSiteEditForm((p) => ({ ...p, latitude: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Longitude"
                  fullWidth
                  value={siteEditForm.longitude}
                  onChange={(e) => setSiteEditForm((p) => ({ ...p, longitude: e.target.value }))}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Leak Night Flow Threshold (L/h)"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.1 }}
                  value={siteEditForm.leakNightFlowThresholdLph}
                  onChange={(e) => setSiteEditForm((p) => ({ ...p, leakNightFlowThresholdLph: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Leak Night Continuous Hours"
                  fullWidth
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={siteEditForm.leakNightContinuousHours}
                  onChange={(e) => setSiteEditForm((p) => ({ ...p, leakNightContinuousHours: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="leakNightStartHour-label">Leak Night Start</InputLabel>
                  <Select
                    labelId="leakNightStartHour-label"
                    label="Leak Night Start"
                    value={siteEditForm.leakNightStartHour}
                    onChange={(e) => setSiteEditForm((p) => ({ ...p, leakNightStartHour: e.target.value }))}
                  >
                    {Array.from({ length: 24 }, (_, h) => (
                      <MenuItem key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="leakNightEndHour-label">Leak Night End</InputLabel>
                  <Select
                    labelId="leakNightEndHour-label"
                    label="Leak Night End"
                    value={siteEditForm.leakNightEndHour}
                    onChange={(e) => setSiteEditForm((p) => ({ ...p, leakNightEndHour: e.target.value }))}
                  >
                    {Array.from({ length: 24 }, (_, i) => i + 1).map((h) => (
                      <MenuItem key={h} value={String(h)}>
                        {String(h).padStart(2, '0')}:00
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Signal Strength Threshold"
                  fullWidth
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  value={siteEditForm.signalStrengthThresholdReadings}
                  onChange={(e) => setSiteEditForm((p) => ({ ...p, signalStrengthThresholdReadings: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSiteEditOpen(false)} disabled={siteEditSaving}>Cancel</Button>
            <Button onClick={handleSiteEditSave} variant="contained" disabled={siteEditSaving}>
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={siteDeleteOpen}
          onClose={() => setSiteDeleteOpen(false)}
          scroll="paper"
          PaperProps={{
            sx: {
              maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle>Delete Site</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this site? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSiteDeleteOpen(false)}>Cancel</Button>
            <Button onClick={handleSiteDeleteConfirm} variant="contained" color="error">Delete</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={siteActionSnackbar.open}
          autoHideDuration={4000}
          onClose={() => setSiteActionSnackbar((p) => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={siteActionSnackbar.severity}
            onClose={() => setSiteActionSnackbar((p) => ({ ...p, open: false }))}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {siteActionSnackbar.message}
          </Alert>
        </Snackbar>
      </>
    </Box>
  );
};

export default SiteDetails;
